import { Group, Layer, Line, Rect, Stage, Text } from "react-konva";
import type { KonvaEventObject } from "konva/lib/Node";
import {
  Fragment,
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import styled from "styled-components";

import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { Color } from "src/elements";
import {
  moveCoordinatesChecker,
  resizeCoordinatesChecker,
} from "../Draggable/utils/coordinatesChecker";
import type { ResizeSide } from "../../store/types";
import { getOuterBounds, getSnappableFixtures } from "../../store/utils";
import { getDividerDrawSpan, SHELF_DIVIDER_COLOR } from "../../common/shelfDividerDraw";
import {
  createContainerMoveOperation,
  createContainerResizeOperation,
  createItemPlacementOperation,
  createItemRangeOperation,
  createPegboardPlacementOperation,
  createShelfPlacementOperation,
  createShelfResizeOperation,
  type ContainerBounds,
  type ContainerMoveOperation,
  type ContainerResizeOperation,
  type ItemPlacementOperation,
  type ItemRangeOperation,
  type ShelfPlacementOperation,
  type ShelfResizeOperation,
  type PlanogramMoveResult,
} from "./operations";
import {
  areSelectionsEqual,
  clearSelection,
  focusEntity,
  focusProduct,
  getNearestPegboardHole,
  PEGBOARD_HOLE_SNAP_TOLERANCE_PX,
  selectContainer,
  hitTestProduct,
  hitTestShelfRow,
  type PlanogramWorldPoint,
  reconcileSelection,
  selectShelf,
  selectPlacement,
  selectPegboard,
  selectProduct,
} from "./selection";
import { getSharedPlanogramAssetCache } from "./assetCache";
import {
  createPlanogramSceneProjector,
  createSplitOffsets,
  layoutAisleBreakCaption,
} from "./sceneProjection";
import { getCanvasLabelFontSize } from "../KonvaViewerPlanogram/labelFit";
import { findSeamAt, getPlanogramSeams } from "./aisleBreakSeams";
import {
  AISLE_BREAK_HANDLE_ATTRIBUTE,
  AisleBreakSeamHandle,
  SEAM_HOVER_HALF_WIDTH_PX,
} from "./AisleBreakSeamHandle";
import {
  createViewportModel,
  DEFAULT_VIEWPORT_BOUNDS,
  getFitScale,
  getFittedViewport,
  resolveViewportWheelIntent,
  type ViewportPoint,
  type ViewportState,
} from "./viewport";
import { resolveOverlayPresentation } from "./overlays";
import { FrontOverhangMarkers } from "./FrontOverhangMarkers";
import {
  getOverlayEntities,
  isWorldRectVisible,
  PlacementCells,
  PresentationOverlays,
  ProductCells,
} from "./KonvaPlanogramPresentation";
import type {
  KonvaPlanogramProps,
  PlanogramContextRequest,
  PlanogramEntityRef,
  PlanogramProductAsset,
  PlanogramPolicy,
  PlanogramScene,
  PlanogramSceneContainer,
  PlanogramSceneFixture,
  PlanogramScenePegboard,
  PlanogramScenePlacement,
  PlanogramSceneProduct,
  PlanogramSceneShelf,
  PlanogramSelection,
  PlanogramViewConfig,
  PlanogramWorldRect,
} from "./types";
import { emptyPlanogramSelection } from "./types";
import { standardPolicy } from "./standardPolicy";

const CanvasRoot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  /* Keep stage width fixed if horizontal overflow triggers a vertical scrollbar. */
  scrollbar-gutter: stable;
  background: ${Color.white};
  overscroll-behavior: contain;
  scrollbar-color: ${Color.primary} ${Color.white};
  touch-action: none;
`;

const CanvasSurface = styled.div.attrs<{ $height: number; $width: number }>(
  ({ $height, $width }) => ({ style: { height: $height, width: $width } }),
)`
  position: relative;
  flex: 0 0 auto;
  min-width: 100%;
  min-height: 100%;
`;

const StageViewport = styled.div<{ $height: number; $width: number }>`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;
  width: ${({ $width }) => `${$width}px`};
  height: ${({ $height }) => `${$height}px`};
  overflow: hidden;
  background: ${Color.white};
`;

const WorldOverlay = styled.div.attrs<{
  $height: number;
  $width: number;
  $viewport: ViewportState;
}>(({ $height, $width, $viewport }) => ({
  style: {
    height: $height,
    width: $width,
    transform: `translate(${$viewport.offsetX}px, ${$viewport.offsetY}px) scale(${$viewport.scale})`,
  },
}))`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  pointer-events: none;
`;

/** Pinned to the stage corner so 1px SR-only boxes cannot grow the scroll area. */
const AccessibleControls = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

const AccessibleProductButton = styled.button`
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
`;

const AccessibleProductGroup = styled.div`
  display: contents;
`;

const AccessibleMoveButton = styled.button`
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
`;

const AccessibleResizeButton = styled.button`
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
`;

const ViewportControlsLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  pointer-events: none;
`;

const ViewportControls = styled.div`
  margin: 8px 8px 0 auto;
  display: flex;
  gap: 4px;
  pointer-events: auto;
`;

const ViewportControl = styled.button`
  min-width: 32px;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid ${Color.spaceGray};
  border-radius: 3px;
  background: ${Color.white};
  color: ${Color.textMain};
  cursor: pointer;
`;

const KonvaPlanogramViewportChrome = memo(
  ({
    labels,
    onReset,
    onZoomIn,
    onZoomOut,
  }: {
    labels: KonvaPlanogramProps["labels"];
    onReset: () => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
  }) => (
    <ViewportControlsLayer data-testid="konva-planogram-viewport-controls-layer">
      <ViewportControls aria-label={labels.viewportControls} role="group">
        <ViewportControl
          aria-label={labels.zoomIn}
          data-testid="konva-planogram-zoom-in"
          onClick={onZoomIn}
          type="button"
        >
          +
        </ViewportControl>
        <ViewportControl
          aria-label={labels.zoomOut}
          data-testid="konva-planogram-zoom-out"
          onClick={onZoomOut}
          type="button"
        >
          −
        </ViewportControl>
        <ViewportControl
          aria-label={labels.resetViewport}
          data-testid="konva-planogram-viewport-reset"
          onClick={onReset}
          type="button"
        >
          {labels.reset}
        </ViewportControl>
      </ViewportControls>
    </ViewportControlsLayer>
  ),
);

const defaultView: Required<
  Pick<
    PlanogramViewConfig,
    "offsetX" | "offsetY" | "productDisplayMode" | "productLabel" | "scale" | "showDividers"
  >
> = {
  offsetX: 0,
  offsetY: 0,
  productDisplayMode: "label",
  productLabel: "name",
  scale: 1,
  showDividers: true,
};

const GESTURE_THRESHOLD = 8;
/** A rack's tiers: distinct from a shelf's board, quietly. */
const RACK_BOARD_FILL = Color.gray600;
const RACK_UPRIGHT_FILL = Color.gray700;
/** World units (x100 in): 0.8 in uprights. */
const RACK_UPRIGHT_WIDTH = 80;

const applyStageTranslation = (
  event: KonvaEventObject<MouseEvent | TouchEvent>,
  viewport: ViewportState,
) => {
  const stage = event.target.getStage?.();
  if (!stage || typeof stage.x !== "function" || typeof stage.y !== "function") return;
  stage.x(viewport.offsetX);
  stage.y(viewport.offsetY);
};

const getAssetKey = (product: PlanogramSceneProduct) => `${product.id}:${product.productCode}`;

const getPlacementAssetKey = (placement: PlanogramScenePlacement) =>
  `${placement.id}:${placement.productId}:${placement.productCode}`;

const combineAccessibleLabels = (label: string, statusLabel: string | null) =>
  statusLabel ? `${label}, ${statusLabel}` : label;

const useStableEvent = <Args extends readonly unknown[], Result>(
  callback: (...args: Args) => Result,
) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  return useMemo(
    () =>
      (...args: Args) =>
        callbackRef.current(...args),
    [],
  );
};

interface KonvaEventNode {
  getAttr: (attribute: string) => unknown;
}

interface KonvaEntityEvent {
  readonly currentTarget?: KonvaEventNode;
  readonly target: KonvaEventNode;
}

const getEntityFromEvent = (event: KonvaEntityEvent): PlanogramEntityRef | null => {
  const node = event.currentTarget ?? event.target;
  const id = node.getAttr("id");
  const name = node.getAttr("name");
  if (typeof id !== "string" || typeof name !== "string") return null;

  if (name.startsWith("product-")) return { id, kind: "product" };
  if (name.startsWith("placement-")) return { id, kind: "placement" };
  if (name.startsWith("pegboard-")) return { id, kind: "pegboard" };
  if (name.startsWith("container-")) return { id, kind: "container" };
  if (name.startsWith("shelf-") || name.startsWith("fixture-")) {
    return { id, kind: "shelf" };
  }

  return null;
};

const getStagePointer = (
  event: KonvaEventObject<DragEvent | MouseEvent | PointerEvent | TouchEvent | WheelEvent>,
): ViewportPoint => {
  const stagePointer = event.target.getStage()?.getPointerPosition();
  if (stagePointer) return stagePointer;

  const touchPoint =
    "touches" in event.evt ? (event.evt.touches[0] ?? event.evt.changedTouches[0]) : undefined;

  if (touchPoint) {
    return { x: touchPoint.clientX, y: touchPoint.clientY };
  }

  if ("clientX" in event.evt) {
    return {
      x:
        "offsetX" in event.evt && Number.isFinite(event.evt.offsetX)
          ? event.evt.offsetX
          : event.evt.clientX,
      y:
        "offsetY" in event.evt && Number.isFinite(event.evt.offsetY)
          ? event.evt.offsetY
          : event.evt.clientY,
    };
  }

  return { x: 0, y: 0 };
};

/** How far past the whole-wall fit zooming out may go. */
const ZOOM_OUT_PAST_FIT = 0.5;

const isSameViewport = (a: ViewportState, b: ViewportState) =>
  Math.abs(a.scale - b.scale) < 1e-6 &&
  Math.abs(a.offsetX - b.offsetX) < 0.5 &&
  Math.abs(a.offsetY - b.offsetY) < 0.5;

/** At fit zoom the world fills the stage — pan only within the slack past that. */
export const clampViewportOffset = (offset: number, contentSize: number, stageSize: number) => {
  const slack = stageSize - contentSize;
  return Math.min(Math.max(offset, Math.min(0, slack)), Math.max(0, slack));
};

type AssetRequest = {
  key: string;
  productCode: string;
  productId: string;
};

const useKeyedAssets = (
  requests: readonly AssetRequest[],
  requestKeys: string,
  assetProvider: KonvaPlanogramProps["assetProvider"],
  enabled: boolean,
) => {
  const [assets, setAssets] = useState<Map<string, PlanogramProductAsset>>(() => new Map());
  const [settledKeys, setSettledKeys] = useState<string | null>(null);
  const activeLoadsRef = useRef<{
    provider: KonvaPlanogramProps["assetProvider"];
    loads: Map<string, Promise<PlanogramProductAsset | null>>;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !assetProvider) {
      activeLoadsRef.current = null;
      return;
    }
    const cache = getSharedPlanogramAssetCache(assetProvider);
    // Keep visible requests (including pending and missing images) independent of LRU eviction.
    const loads =
      activeLoadsRef.current?.provider === assetProvider
        ? activeLoadsRef.current.loads
        : new Map<string, Promise<PlanogramProductAsset | null>>();
    activeLoadsRef.current = { provider: assetProvider, loads };
    const liveKeys = new Set(requests.map((request) => request.key));
    for (const key of loads.keys()) {
      if (!liveKeys.has(key)) loads.delete(key);
    }
    let cancelled = false;
    let remaining = requests.length;
    let publishFrame: number | null = null;
    const pendingAssets = new Map<string, PlanogramProductAsset>();

    const publish = () => {
      publishFrame = null;
      if (cancelled) return;

      // Batch image arrivals to avoid cloning the entire map once per image.
      if (pendingAssets.size > 0) {
        const resolvedAssets = new Map(pendingAssets);
        pendingAssets.clear();
        setAssets((current) => {
          let changed = false;
          const next = new Map(current);
          for (const [key, asset] of resolvedAssets) {
            if (next.get(key) === asset) continue;
            next.set(key, asset);
            changed = true;
          }
          return changed ? next : current;
        });
      }

      // Publish settled only after the asset batch in this frame has been
      // queued, so consumers never observe completion before its images.
      if (remaining === 0) setSettledKeys(requestKeys);
    };

    const schedulePublish = () => {
      if (publishFrame !== null) return;
      publishFrame = requestAnimationFrame(publish);
    };

    // Empty lists must settle here; a later effect used to clear this and never finish.
    if (remaining === 0) {
      setSettledKeys(requestKeys);
      return;
    }

    setSettledKeys(null);

    for (const request of requests) {
      let load = loads.get(request.key);
      if (!load) {
        load = cache.load(assetProvider, {
          productCode: request.productCode,
          productId: request.productId,
        });
        loads.set(request.key, load);
      }
      void load.then((asset) => {
        if (cancelled) return;
        if (asset) {
          pendingAssets.set(request.key, asset);
        }
        remaining -= 1;
        schedulePublish();
      });
    }

    return () => {
      cancelled = true;
      if (publishFrame !== null) {
        cancelAnimationFrame(publishFrame);
        publishFrame = null;
      }
    };
  }, [assetProvider, enabled, requestKeys]);

  useEffect(() => {
    const liveKeys = new Set(requests.map((request) => request.key));
    setAssets((current) => {
      const next = new Map([...current].filter(([key]) => liveKeys.has(key)));
      return next.size === current.size ? current : next;
    });
  }, [requestKeys]);

  useEffect(() => {
    setAssets(new Map());
  }, [assetProvider]);

  const settled = !enabled || !assetProvider || settledKeys === requestKeys;
  const visibleAssets = useMemo(() => {
    if (!enabled || !assetProvider) return assets;
    const cache = getSharedPlanogramAssetCache(assetProvider);
    const next = new Map(assets);
    // Returning nodes can use decoded images immediately, before the next asset publication frame.
    for (const request of requests) {
      if (next.has(request.key)) continue;
      const cached = cache.get(assetProvider, request);
      if (cached?.status === "resolved" && cached.asset) next.set(request.key, cached.asset);
    }
    return next;
  }, [assets, assetProvider, enabled, requestKeys]);

  return { assets: visibleAssets, settled };
};

const useProductAssets = (
  products: PlanogramSceneProduct[],
  assetProvider: KonvaPlanogramProps["assetProvider"],
  enabled: boolean,
) =>
  useKeyedAssets(
    products.map((product) => ({
      key: getAssetKey(product),
      productCode: product.productCode,
      productId: product.id,
    })),
    products.map(getAssetKey).join("|"),
    assetProvider,
    enabled,
  );

const usePlacementAssets = (
  placements: readonly PlanogramScenePlacement[],
  assetProvider: KonvaPlanogramProps["assetProvider"],
  enabled: boolean,
) =>
  useKeyedAssets(
    placements.map((placement) => ({
      key: getPlacementAssetKey(placement),
      productCode: placement.productCode,
      productId: placement.productId,
    })),
    placements.map(getPlacementAssetKey).join("|"),
    assetProvider,
    enabled,
  );

const isSelected = (selection: PlanogramSelection, productId: string) =>
  selection.kind === "product" && selection.ids.includes(productId);

const getProductEntity = (product: PlanogramSceneProduct): PlanogramEntityRef => ({
  id: product.id,
  kind: "product",
});

const getPlacementEntity = (placement: PlanogramScenePlacement): PlanogramEntityRef => ({
  id: placement.id,
  kind: "placement",
});

const getPegboardEntity = (pegboard: PlanogramScenePegboard): PlanogramEntityRef => ({
  id: pegboard.id,
  kind: "pegboard",
});

const getContainerEntity = (container: PlanogramSceneContainer): PlanogramEntityRef => ({
  id: container.id,
  kind: "container",
});

interface ActiveShelfDrag {
  readonly originalPosition: ViewportPoint;
  readonly restoreVisual: () => void;
  readonly stopDrag: () => void;
  readonly selection: PlanogramSelection;
  readonly shelfId: string;
  readonly shelfIds: readonly string[];
  readonly startWorldY: number;
  completed: boolean;
}

interface ShelfDragPreview {
  readonly activeShelfId: string;
  readonly deltaY: number;
  readonly shelfIds: readonly string[];
}

const resolveShelfDragMotion = (
  activeShelf: PlanogramShelf,
  movingShelves: readonly PlanogramShelf[],
  startWorldY: number,
  pointerWorldY: number,
) => {
  const requestedDeltaNotches = Math.round(
    (startWorldY - pointerWorldY) / Math.max(activeShelf.bay.notchSpacing, 1),
  );
  const minimumDeltaNotches = Math.max(...movingShelves.map((shelf) => 1 - shelf.getNotchNo()));
  const maximumDeltaNotches = Math.min(
    ...movingShelves.map((shelf) => shelf.bay.maxNotch - shelf.getNotchNo()),
  );
  const deltaNotches = Math.max(
    minimumDeltaNotches,
    Math.min(maximumDeltaNotches, requestedDeltaNotches),
  );

  return {
    deltaY: -deltaNotches * activeShelf.bay.notchSpacing,
    targetNotch: activeShelf.getNotchNo() + deltaNotches,
  };
};

interface ActivePlacementDrag {
  readonly activePlacementId: string;
  readonly placementIds: readonly string[];
  readonly restoreVisual: () => void;
  readonly selection: PlanogramSelection;
  completed: boolean;
}

interface ActiveContainerDrag {
  readonly containerId: string;
  readonly restoreVisual: () => void;
  readonly selection: PlanogramSelection;
  readonly startWorld: ViewportPoint;
  completed: boolean;
}

interface ActiveContainerResize {
  readonly containerId: string;
  readonly restoreVisual: () => void;
  readonly selection: PlanogramSelection;
  readonly side: ResizeSide;
  completed: boolean;
}

const CONTAINER_RESIZE_SIDES: readonly ResizeSide[] = [
  "top-left",
  "top",
  "top-right",
  "right",
  "bottom-right",
  "bottom",
  "bottom-left",
  "left",
];

const getContainerResizeHandlePosition = (
  container: PlanogramSceneContainer,
  side: ResizeSide,
  handleSize: number,
): ViewportPoint => {
  const left = container.x;
  const centerX = container.x + container.width / 2 - handleSize / 2;
  const right = container.x + container.width - handleSize;
  const top = container.y;
  const middleY = container.y + container.height / 2 - handleSize / 2;
  const bottom = container.y + container.height - handleSize;

  switch (side) {
    case "top-left":
      return { x: left, y: top };
    case "top":
      return { x: centerX, y: top };
    case "top-right":
      return { x: right, y: top };
    case "right":
      return { x: right, y: middleY };
    case "bottom-right":
      return { x: right, y: bottom };
    case "bottom":
      return { x: centerX, y: bottom };
    case "bottom-left":
      return { x: left, y: bottom };
    case "left":
      return { x: left, y: middleY };
  }
};

const resizesLeft = (side: ResizeSide) =>
  side === "left" || side === "top-left" || side === "bottom-left";
const resizesRight = (side: ResizeSide) =>
  side === "right" || side === "top-right" || side === "bottom-right";
const resizesTop = (side: ResizeSide) =>
  side === "top" || side === "top-left" || side === "top-right";
const resizesBottom = (side: ResizeSide) =>
  side === "bottom" || side === "bottom-left" || side === "bottom-right";

interface ActiveProductDrag {
  readonly activeItemId: string;
  readonly cancelVisual: () => void;
  readonly intent: "copy" | "move";
  readonly itemIds: readonly string[];
  readonly restoreVisual: () => void;
  readonly selection: PlanogramSelection;
  completed: boolean;
}

interface ProductDragPreview {
  readonly activeItemId: string;
  readonly itemIds: readonly string[];
}

const getInsertionSide = (product: PlanogramSceneProduct, worldPoint: ViewportPoint) =>
  worldPoint.x < product.x + product.width / 2 ? "left" : "right";

interface ProductDropHint {
  readonly caretX: number;
  readonly width: number;
  readonly y: number;
  readonly height: number;
  readonly targetId: string;
}

interface DragSimulation {
  readonly key: string;
  readonly scene: PlanogramScene;
  readonly hint: ProductDropHint | null;
}

type RelativeMoveDirection = "left" | "right";
type RelativeMoveTargets = ReadonlyMap<
  string,
  Partial<Record<RelativeMoveDirection, PlanogramSceneProduct>>
>;

export const createRelativeMoveTargets = (
  products: readonly PlanogramSceneProduct[],
  selectedProductIds: Pick<ReadonlySet<string>, "has">,
): RelativeMoveTargets => {
  const groups = new Map<string, PlanogramSceneProduct[]>();
  const targets = new Map<string, Partial<Record<RelativeMoveDirection, PlanogramSceneProduct>>>();

  for (const product of products) {
    const group = groups.get(product.shelfGroupId) ?? [];
    group.push(product);
    groups.set(product.shelfGroupId, group);
  }

  for (const group of groups.values()) {
    const previousUnselected: Array<PlanogramSceneProduct | undefined> = [];
    const nextUnselected: Array<PlanogramSceneProduct | undefined> = [];
    let nearest: PlanogramSceneProduct | undefined;
    for (let index = 0; index < group.length; index += 1) {
      const product = group[index];
      previousUnselected[index] = nearest;
      if (product && !selectedProductIds.has(product.id)) nearest = product;
    }

    nearest = undefined;
    for (let index = group.length - 1; index >= 0; index -= 1) {
      const product = group[index];
      nextUnselected[index] = nearest;
      if (product && !selectedProductIds.has(product.id)) nearest = product;
    }

    for (let index = 0; index < group.length; index += 1) {
      const product = group[index];
      if (!product) continue;
      const isSelected = selectedProductIds.has(product.id);
      targets.set(product.id, {
        left: isSelected ? previousUnselected[index] : group[index - 1],
        right: isSelected ? nextUnselected[index] : group[index + 1],
      });
    }
  }

  return targets;
};

const getDragSelection = (
  scene: Parameters<typeof selectProduct>[0],
  selection: PlanogramSelection,
  productId: string,
) => (isSelected(selection, productId) ? selection : selectProduct(scene, selection, productId));

/**
 * Empty shelf space is a drop target only for moving products onto another shelf.
 * Space on the shelf every dragged product already sits on (including the dragged
 * products' own footprint) stays a missing target, as before shelf drops existed.
 */
const hitTestOtherShelfRow = (
  scene: PlanogramScene,
  worldPoint: PlanogramWorldPoint,
  draggedItemIds: readonly string[],
) => {
  const shelf = hitTestShelfRow(scene, worldPoint);
  if (!shelf) return null;
  const dragged = new Set(draggedItemIds);
  const alreadyThere = scene.products
    .filter((candidate) => dragged.has(candidate.id))
    .every((candidate) => candidate.shelfId === shelf.id);
  return alreadyThere ? null : shelf;
};

export const KonvaPlanogram = ({
  centerOnFit = false,
  aisleBreakEditing,
  assetProvider,
  dropPreview,
  labels,
  onActivate,
  onAssetsSettled,
  onContextRequest,
  onOverlayBadgeActivate,
  overlayBadges,
  overlayChannels,
  onCommit,
  onHoverChange,
  onMoveResult,
  onSelectionChange,
  onViewportChange,
  openFitted = false,
  policy = standardPolicy,
  pog,
  scrollToEntityId,
  selection = emptyPlanogramSelection,
  stageRef,
  viewport,
  viewportConfig,
  view,
  worldOverlay,
}: KonvaPlanogramProps) => {
  const resolvedView = { ...defaultView, ...view };
  const panPointerRef = useRef<ViewportPoint | null>(null);
  const panOriginRef = useRef<ViewportPoint | null>(null);
  const backgroundPanMovedRef = useRef(false);
  const activeProductDragRef = useRef<ActiveProductDrag | null>(null);
  const activePlacementDragRef = useRef<ActivePlacementDrag | null>(null);
  const activeShelfDragRef = useRef(new Map<string, ActiveShelfDrag>());
  const activeContainerDragRef = useRef<ActiveContainerDrag | null>(null);
  const activeContainerResizeRef = useRef<ActiveContainerResize | null>(null);
  const [productDragPreview, setProductDragPreview] = useState<ProductDragPreview | null>(null);
  const [shelfDragPreview, setShelfDragPreview] = useState<ShelfDragPreview | null>(null);
  const productDragPreviewIds = useMemo(
    () => new Set(productDragPreview?.itemIds ?? []),
    [productDragPreview?.itemIds],
  );
  const shelfDragPreviewIds = useMemo(
    () => new Set(shelfDragPreview?.shelfIds ?? []),
    [shelfDragPreview?.shelfIds],
  );
  const activePlacementDrag = activePlacementDragRef.current;
  const activePlacementDragIds = useMemo(
    () => new Set(activePlacementDrag?.placementIds ?? []),
    [activePlacementDrag],
  );
  const setShelfDragPreviewIfChanged = (next: ShelfDragPreview) => {
    setShelfDragPreview((current) =>
      current?.activeShelfId === next.activeShelfId &&
      current.deltaY === next.deltaY &&
      current.shelfIds === next.shelfIds
        ? current
        : next,
    );
  };
  const [dropHint, setDropHint] = useState<ProductDropHint | null>(null);
  const [dragSimulation, setDragSimulation] = useState<DragSimulation | null>(null);
  const setDragSimulationIfChanged = (next: DragSimulation | null) => {
    setDragSimulation((current) => {
      if (current === next) return current;
      if (current && next && current.key === next.key) return current;
      return next;
    });
  };
  /** Pointer moves fire continuously; only a changed seam should re-render. */
  const setDropHintIfChanged = (next: ProductDropHint | null) => {
    setDropHint((current) => {
      if (current === next) return current;
      if (
        current &&
        next &&
        current.caretX === next.caretX &&
        current.width === next.width &&
        current.y === next.y &&
        current.height === next.height &&
        current.targetId === next.targetId
      ) {
        return current;
      }
      return next;
    });
  };
  const [hoveredEntity, setHoveredEntityState] = useState<PlanogramEntityRef | null>(null);
  const hoveredId = hoveredEntity?.id ?? null;
  const canvasRootRef = useRef<HTMLDivElement>(null);
  const worldOverlayRef = useRef<HTMLDivElement>(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const root = canvasRootRef.current;
    if (!root) return;

    const updateSize = () => {
      const rect = root.getBoundingClientRect();
      const height = root.clientHeight || rect.height;
      const width = root.clientWidth || rect.width;
      setViewportSize((current) =>
        current.height === height && current.width === width ? current : { height, width },
      );
    };

    updateSize();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateSize);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);
  const productButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const pegboardButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const placementButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const [sceneProjector] = useState(createPlanogramSceneProjector);
  const scene = useMemo(
    () => sceneProjector.project(pog, resolvedView.productLabel),
    [pog, resolvedView.productLabel, sceneProjector],
  );
  // Aisle breaks put gaps in the scene that pog space does not have, so a
  // pointer on the stage has to be read back into pog units before it is
  // compared with anything the pog holds.
  const splitOffsets = useMemo(() => createSplitOffsets(pog.planogram.bays), [pog]);
  const renderedWorldOverlay = useMemo(
    () => (typeof worldOverlay === "function" ? worldOverlay({ shelfDragPreview }) : worldOverlay),
    [shelfDragPreview, worldOverlay],
  );
  const renderedProducts = useMemo(() => {
    if (!dragSimulation) return scene.products;
    const simulated = new Map(dragSimulation.scene.products.map((item) => [item.id, item]));
    return scene.products.map((product) =>
      productDragPreviewIds.has(product.id) ? product : (simulated.get(product.id) ?? product),
    );
  }, [dragSimulation, productDragPreviewIds, scene.products]);
  const projectionStats = sceneProjector.getStats();
  const getShelfDragOffset = (shelfId: string | null) =>
    shelfId && shelfDragPreviewIds.has(shelfId) ? (shelfDragPreview?.deltaY ?? 0) : 0;
  const scenePegboards = scene.pegboards ?? [];
  const selectablePegboards = scenePegboards.filter(({ source }) => source === "planogram");
  const scenePlacements = scene.placements ?? [];
  const activeSelection = useMemo(() => reconcileSelection(selection, scene), [scene, selection]);
  const selectedProductIds = useMemo(
    () => (activeSelection.kind === "product" ? new Set(activeSelection.ids) : new Set<string>()),
    [activeSelection],
  );
  const selectedContainerIds = useMemo(
    () => (activeSelection.kind === "container" ? new Set(activeSelection.ids) : new Set<string>()),
    [activeSelection],
  );
  const selectedPegboardIds = useMemo(
    () => (activeSelection.kind === "pegboard" ? new Set(activeSelection.ids) : new Set<string>()),
    [activeSelection],
  );
  const selectedPlacementIds = useMemo(
    () => (activeSelection.kind === "placement" ? new Set(activeSelection.ids) : new Set<string>()),
    [activeSelection],
  );
  const selectedShelfIds = useMemo(
    () => (activeSelection.kind === "shelf" ? new Set(activeSelection.ids) : new Set<string>()),
    [activeSelection],
  );
  const relativeMoveTargets = useMemo(
    () => createRelativeMoveTargets(scene.products, selectedProductIds),
    [scene.products, selectedProductIds],
  );
  const overlayEntities = useMemo(() => getOverlayEntities(scene), [scene]);
  const worldWidth = resolvedView.width ?? scene.width;
  const worldHeight = resolvedView.height ?? scene.height;
  const worldSize = { width: worldWidth, height: worldHeight };
  // The whole wall in view, once the canvas has a size. Zooming out always reaches it and
  // a little past it, whatever floor the host set: a host that opens at its own minimum,
  // or fitted, would otherwise leave the zoom-out button with nothing to do.
  const fitScale = getFitScale(viewportSize, worldSize);
  const configuredMinScale = viewportConfig?.bounds?.minScale;
  const configuredMaxScale = viewportConfig?.bounds?.maxScale;
  const viewportBounds = useMemo(() => {
    const bounds = {
      maxScale: configuredMaxScale ?? DEFAULT_VIEWPORT_BOUNDS.maxScale,
      minScale: configuredMinScale ?? DEFAULT_VIEWPORT_BOUNDS.minScale,
    };
    if (fitScale === null) return bounds;
    const minScale = Math.min(bounds.minScale, fitScale * ZOOM_OUT_PAST_FIT);
    return { maxScale: Math.max(bounds.maxScale, minScale), minScale };
  }, [configuredMaxScale, configuredMinScale, fitScale]);
  const viewportModel = useMemo(
    () =>
      createViewportModel({
        ...viewportConfig,
        bounds: viewportBounds,
        defaultState: {
          offsetX: resolvedView.offsetX,
          offsetY: resolvedView.offsetY,
          scale: resolvedView.scale,
          ...viewportConfig?.defaultState,
        },
      }),
    [
      resolvedView.offsetX,
      resolvedView.offsetY,
      resolvedView.scale,
      viewportBounds,
      viewportConfig?.defaultState?.offsetX,
      viewportConfig?.defaultState?.offsetY,
      viewportConfig?.defaultState?.scale,
    ],
  );
  const [uncontrolledViewport, setUncontrolledViewport] = useState<ViewportState>(
    () => viewportModel.defaultState,
  );
  const previousDefaultScale = useRef(viewportModel.defaultState.scale);
  useEffect(() => {
    if (viewport) return;

    const previousScale = previousDefaultScale.current;
    previousDefaultScale.current = viewportModel.defaultState.scale;
    setUncontrolledViewport((current) =>
      viewportModel.createState({
        ...current,
        scale: current.scale * (viewportModel.defaultState.scale / previousScale),
      }),
    );
  }, [viewport, viewportModel]);
  const rawActiveViewport = viewport ? viewportModel.createState(viewport) : uncontrolledViewport;
  const liveViewportRef = useRef(rawActiveViewport);
  const publishedViewportRef = useRef(rawActiveViewport);
  const viewportFrameRef = useRef<number | null>(null);
  const viewportAnimationRef = useRef<{
    from: ViewportState;
    target: ViewportState;
    startedAt: number;
    kind: "pan" | "zoom" | "button-zoom";
  } | null>(null);
  useEffect(
    () => () => {
      if (viewportFrameRef.current !== null) cancelAnimationFrame(viewportFrameRef.current);
    },
    [],
  );
  const dpr = typeof window === "undefined" ? 1 : Math.max(1, window.devicePixelRatio || 1);
  const maxCanvasSide = 32767 / dpr;
  const maxCanvasArea = 268435456 / (dpr * dpr);
  const fallbackStageHeight = Math.min(
    maxCanvasSide,
    Math.max(1, worldHeight * viewportModel.defaultState.scale),
  );
  const fallbackStageWidth = Math.min(
    maxCanvasSide,
    maxCanvasArea / fallbackStageHeight,
    Math.max(1, worldWidth * viewportModel.defaultState.scale),
  );
  const stageWidth = Math.max(1, viewportSize.width || fallbackStageWidth);
  const stageHeight = Math.max(1, viewportSize.height || fallbackStageHeight);
  const isViewportMeasured = viewportSize.width > 0 && viewportSize.height > 0;
  const centerAtFit =
    centerOnFit &&
    Math.abs(rawActiveViewport.scale / viewportModel.defaultState.scale - 1) < 0.000001;
  const centeredOffset = (offset: number, contentSize: number, stageSize: number) =>
    centerAtFit && contentSize <= stageSize ? (stageSize - contentSize) / 2 : offset;
  const activeViewport = viewportModel.createState({
    ...rawActiveViewport,
    offsetX: clampViewportOffset(
      centeredOffset(rawActiveViewport.offsetX, worldWidth * rawActiveViewport.scale, stageWidth),
      worldWidth * rawActiveViewport.scale,
      stageWidth,
    ),
    offsetY: clampViewportOffset(
      centeredOffset(rawActiveViewport.offsetY, worldHeight * rawActiveViewport.scale, stageHeight),
      worldHeight * rawActiveViewport.scale,
      stageHeight,
    ),
  });
  if (!panPointerRef.current && viewportFrameRef.current === null) {
    liveViewportRef.current = activeViewport;
  }
  const surfaceWidth = Math.max(1, worldWidth * activeViewport.scale, stageWidth);
  const surfaceHeight = Math.max(1, worldHeight * activeViewport.scale, stageHeight);
  const [hoveredSeam, setHoveredSeam] = useState<{ bayId: string; worldY: number } | null>(null);
  const isEditingAisleBreaks = aisleBreakEditing !== undefined;
  const seams = useMemo(
    () =>
      isEditingAisleBreaks
        ? getPlanogramSeams(scene, SEAM_HOVER_HALF_WIDTH_PX / activeViewport.scale)
        : [],
    [isEditingAisleBreaks, activeViewport.scale, scene],
  );
  const hoveredSeamView = hoveredSeam
    ? seams.find((seam) => seam.bayId === hoveredSeam.bayId)
    : undefined;
  const handleSeamPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!aisleBreakEditing) return;
    // A pan or a drag is under way: no handle until the button is up.
    if (event.buttons > 0) {
      setHoveredSeam(null);
      return;
    }
    const target = event.target as Element | null;
    if (target?.closest?.(`[${AISLE_BREAK_HANDLE_ATTRIBUTE}]`)) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const stagePoint = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const worldPoint = viewportModel.stageToWorld(activeViewport, stagePoint);
    const seam = findSeamAt(seams, worldPoint);
    setHoveredSeam((current) => {
      if (!seam) return null;
      return current?.bayId === seam.bayId ? current : { bayId: seam.bayId, worldY: worldPoint.y };
    });
  };
  const showProductLabels =
    resolvedView.productDisplayMode === "label" && resolvedView.productLabel !== "none";
  const globalLabelFontSize = useMemo(() => getCanvasLabelFontSize(worldHeight), [worldHeight]);
  const isProductVisible = (product: PlanogramSceneProduct) =>
    isWorldRectVisible(product, activeViewport, stageWidth, stageHeight);
  // A small buffer keeps nearby images ready without mounting the entire run.
  const renderWidth =
    viewportSize.width ||
    (onAssetsSettled || typeof window === "undefined"
      ? stageWidth
      : Math.min(stageWidth, window.innerWidth));
  const renderLeft = (-activeViewport.offsetX - renderWidth / 4) / activeViewport.scale;
  const renderRight = (renderWidth * 1.25 - activeViewport.offsetX) / activeViewport.scale;
  const isInRenderWindow = (rect: PlanogramWorldRect) =>
    rect.x < renderRight && rect.x + rect.width > renderLeft;
  const visibleProducts = renderedProducts.filter(
    (product) =>
      isInRenderWindow(product) ||
      productDragPreviewIds.has(product.id) ||
      shelfDragPreviewIds.has(product.shelfId),
  );
  const visiblePlacements = scenePlacements.filter(
    (placement) => isInRenderWindow(placement) || activePlacementDragIds.has(placement.id),
  );
  const visibleOverlayEntities = overlayEntities.filter(isInRenderWindow);
  const visibleOverlayIds = new Set(visibleOverlayEntities.map((entity) => entity.id));

  const overlayPresentations = useMemo(
    () =>
      resolveOverlayPresentation({
        badges: overlayBadges,
        channels: overlayChannels,
        entityIds: overlayEntities.map((entity) => entity.id),
        hoveredId,
        selection: {
          focusedId: activeSelection.focusedId,
          ids: activeSelection.ids,
        },
      }),
    [
      activeSelection.focusedId,
      activeSelection.ids,
      hoveredId,
      overlayBadges,
      overlayChannels,
      overlayEntities,
    ],
  );
  const { assets, settled: productAssetsSettled } = useProductAssets(
    onAssetsSettled ? scene.products : visibleProducts,
    assetProvider,
    resolvedView.productDisplayMode === "image",
  );
  const { assets: placementAssets, settled: placementAssetsSettled } = usePlacementAssets(
    onAssetsSettled ? scenePlacements : visiblePlacements,
    assetProvider,
    resolvedView.productDisplayMode === "image",
  );
  const assetsSettled = productAssetsSettled && placementAssetsSettled;
  const emitAssetsSettled = useStableEvent(() => onAssetsSettled?.());
  useEffect(() => {
    if (assetsSettled) emitAssetsSettled();
  }, [assetsSettled, emitAssetsSettled, scene]);

  const clampViewportState = (nextViewport: ViewportState) => {
    const nextState = viewportModel.createState(nextViewport);
    return {
      ...nextState,
      offsetX: clampViewportOffset(nextState.offsetX, worldWidth * nextState.scale, stageWidth),
      offsetY: clampViewportOffset(nextState.offsetY, worldHeight * nextState.scale, stageHeight),
    };
  };

  const syncScrollPosition = (nextViewport: ViewportState) => {
    const root = canvasRootRef.current;
    if (!root) return;

    const nextScrollLeft = Math.max(0, -nextViewport.offsetX);
    const nextScrollTop = Math.max(0, -nextViewport.offsetY);
    if (root.scrollLeft !== nextScrollLeft) root.scrollLeft = nextScrollLeft;
    if (root.scrollTop !== nextScrollTop) root.scrollTop = nextScrollTop;
  };

  const updateViewport = (nextViewport: ViewportState) => {
    viewportAnimationRef.current = null;
    if (viewportFrameRef.current !== null) {
      cancelAnimationFrame(viewportFrameRef.current);
      viewportFrameRef.current = null;
    }
    const nextState = clampViewportState(nextViewport);
    liveViewportRef.current = nextState;
    if (!viewport) setUncontrolledViewport(nextState);
    publishedViewportRef.current = nextState;
    onViewportChange?.(nextState);
  };
  const publishViewportFrame = useStableEvent((now: number) => {
    viewportFrameRef.current = null;
    const animation = viewportAnimationRef.current;
    if (!animation) {
      updateViewport(liveViewportRef.current);
      return;
    }
    const elapsed = Math.max(0, now - animation.startedAt);
    const progress = Math.min(1, elapsed / 140);
    const { from, target } = animation;
    const finished =
      progress === 1 ||
      (animation.kind !== "button-zoom" &&
        Math.abs(target.scale - from.scale) < 0.0001 &&
        Math.hypot(target.offsetX - from.offsetX, target.offsetY - from.offsetY) < 0.25);
    const eased = finished
      ? 1
      : animation.kind !== "button-zoom"
        ? 1 - Math.exp(-elapsed / 35)
        : 1 - (1 - progress) ** 3;
    updateViewport({
      scale: from.scale + (target.scale - from.scale) * eased,
      offsetX: from.offsetX + (target.offsetX - from.offsetX) * eased,
      offsetY: from.offsetY + (target.offsetY - from.offsetY) * eased,
    });
    if (!finished) {
      viewportAnimationRef.current =
        animation.kind !== "button-zoom"
          ? { ...animation, from: liveViewportRef.current, startedAt: now }
          : animation;
      viewportFrameRef.current = requestAnimationFrame(publishViewportFrame);
    }
  });
  const animateViewport = (
    nextViewport: ViewportState,
    kind: "pan" | "zoom" | "button-zoom" = "button-zoom",
  ) => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      updateViewport(nextViewport);
      return;
    }
    const previous = viewportAnimationRef.current;
    viewportAnimationRef.current = {
      from: liveViewportRef.current,
      target: clampViewportState(nextViewport),
      // Continuous scrolling advances by frame time, not time since the latest wheel event.
      startedAt:
        kind !== "button-zoom" && previous?.kind === kind ? previous.startedAt : performance.now(),
      kind,
    };
    if (viewportFrameRef.current === null) {
      viewportFrameRef.current = requestAnimationFrame(publishViewportFrame);
    }
  };
  const scheduleViewport = (nextViewport: ViewportState) => {
    viewportAnimationRef.current = null;
    const nextState = clampViewportState(nextViewport);
    const current = liveViewportRef.current;
    if (
      nextState.offsetX === current.offsetX &&
      nextState.offsetY === current.offsetY &&
      nextState.scale === current.scale
    )
      return;
    liveViewportRef.current = nextState;
    if (viewportFrameRef.current === null) {
      viewportFrameRef.current = requestAnimationFrame(publishViewportFrame);
    }
  };
  const zoomIn = useStableEvent(() => {
    animateViewport(
      viewportModel.zoomBy(viewportAnimationRef.current?.target ?? liveViewportRef.current, 1.1, {
        x: stageWidth / 2,
        y: stageHeight / 2,
      }),
    );
  });
  const zoomOut = useStableEvent(() => {
    animateViewport(
      viewportModel.zoomBy(
        viewportAnimationRef.current?.target ?? liveViewportRef.current,
        1 / 1.1,
        {
          x: stageWidth / 2,
          y: stageHeight / 2,
        },
      ),
    );
  });
  // Opening fitted: the whole wall in view, centred. The canvas can measure before its
  // popup has finished laying out, so the fit follows the canvas size until the viewer
  // zooms or pans — after that it is theirs. `applied` is the fit last set, so any other
  // live viewport means they have moved.
  const fittedViewport = openFitted ? getFittedViewport(viewportSize, worldSize) : null;
  const autoFitRef = useRef<{ following: boolean; applied: ViewportState | null }>({
    following: openFitted,
    applied: null,
  });
  const applyFittedViewport = (fit: ViewportState) => {
    updateViewport(fit);
    autoFitRef.current = { following: true, applied: liveViewportRef.current };
  };
  useLayoutEffect(() => {
    const autoFit = autoFitRef.current;
    if (!fittedViewport || !autoFit.following) return;
    if (autoFit.applied && !isSameViewport(liveViewportRef.current, autoFit.applied)) {
      autoFitRef.current = { following: false, applied: null };
      return;
    }
    applyFittedViewport(fittedViewport);
  }, [fittedViewport?.offsetX, fittedViewport?.offsetY, fittedViewport?.scale]);
  const resetViewport = useStableEvent(() =>
    fittedViewport ? applyFittedViewport(fittedViewport) : updateViewport(viewportModel.reset()),
  );

  const handleViewportScroll = () => {
    const root = canvasRootRef.current;
    if (!root || !isViewportMeasured) return;

    const current = liveViewportRef.current;
    const nextViewport = clampViewportState({
      ...current,
      offsetX: surfaceWidth > stageWidth ? -root.scrollLeft : current.offsetX,
      offsetY: surfaceHeight > stageHeight ? -root.scrollTop : current.offsetY,
    });
    if (
      Math.abs(nextViewport.offsetX - current.offsetX) < 1 &&
      Math.abs(nextViewport.offsetY - current.offsetY) < 1 &&
      nextViewport.scale === current.scale
    ) {
      return;
    }

    scheduleViewport(nextViewport);
  };

  useLayoutEffect(() => {
    if (viewportAnimationRef.current) updateViewport(activeViewport);
  }, [viewportModel, stageWidth, stageHeight]);

  useLayoutEffect(() => {
    if (!isViewportMeasured) return;
    syncScrollPosition(activeViewport);
  }, [
    isViewportMeasured,
    activeViewport.offsetX,
    activeViewport.offsetY,
    activeViewport.scale,
    stageHeight,
    stageWidth,
    surfaceHeight,
    surfaceWidth,
  ]);

  useLayoutEffect(() => {
    // The fallback stage spans the whole plan; clamping against it before measurement
    // would erase an initial bay offset before the real scrollable viewport exists.
    if (!isViewportMeasured) return;
    if (
      rawActiveViewport.offsetX !== activeViewport.offsetX ||
      rawActiveViewport.offsetY !== activeViewport.offsetY
    ) {
      updateViewport(activeViewport);
    }
  }, [
    isViewportMeasured,
    rawActiveViewport.offsetX,
    rawActiveViewport.offsetY,
    activeViewport.offsetX,
    activeViewport.offsetY,
  ]);

  useLayoutEffect(() => {
    if (!isViewportMeasured) return;
    const published = publishedViewportRef.current;
    if (
      !viewport &&
      (published.scale !== activeViewport.scale ||
        published.offsetX !== activeViewport.offsetX ||
        published.offsetY !== activeViewport.offsetY)
    ) {
      publishedViewportRef.current = activeViewport;
      onViewportChange?.(activeViewport);
    }
  }, [
    activeViewport.scale,
    activeViewport.offsetX,
    activeViewport.offsetY,
    viewport,
    isViewportMeasured,
  ]);

  useEffect(() => {
    if (!areSelectionsEqual(selection, activeSelection)) {
      onSelectionChange?.(activeSelection);
    }
  }, [activeSelection, onSelectionChange, selection]);

  const updateSelection = (nextSelection: PlanogramSelection) => {
    if (!areSelectionsEqual(activeSelection, nextSelection)) {
      onSelectionChange?.(nextSelection);
    }
  };

  const setHoveredEntity = (entity: PlanogramEntityRef) => setHoveredEntityState(entity);
  const clearHoveredEntity = (id: string) =>
    setHoveredEntityState((current) => (current?.id === id ? null : current));

  const emitHoverChange = useStableEvent((entity: PlanogramEntityRef | null) =>
    onHoverChange?.(entity),
  );
  useEffect(() => {
    emitHoverChange(hoveredEntity);
  }, [emitHoverChange, hoveredEntity]);
  useEffect(() => () => emitHoverChange(null), [emitHoverChange]);

  const lastScrolledEntityRef = useRef<{ id: string | null; measured: boolean }>({
    id: null,
    measured: false,
  });
  useEffect(() => {
    const nextEntityId = scrollToEntityId ?? null;
    const measured = viewportSize.width > 0 && viewportSize.height > 0;
    const previous = lastScrolledEntityRef.current;
    if (nextEntityId === previous.id && (previous.measured || !measured)) return;
    lastScrolledEntityRef.current = { id: nextEntityId, measured };
    if (!nextEntityId) return;
    // The whole wall is already in view; panning would only push it off-centre.
    if (autoFitRef.current.following && fittedViewport) return;

    const entity =
      scene.products.find((candidate) => candidate.id === nextEntityId) ??
      scenePlacements.find((candidate) => candidate.id === nextEntityId) ??
      scene.containers.find((candidate) => candidate.id === nextEntityId);
    if (!entity) return;

    const center = { x: entity.x + entity.width / 2, y: entity.y + entity.height / 2 };
    const nextViewport = viewportModel.panTo(activeViewport, {
      x: clampViewportOffset(
        stageWidth / 2 - center.x * activeViewport.scale,
        worldWidth * activeViewport.scale,
        stageWidth,
      ),
      y: clampViewportOffset(
        stageHeight / 2 - center.y * activeViewport.scale,
        worldHeight * activeViewport.scale,
        stageHeight,
      ),
    });

    updateViewport(nextViewport);
  });

  const startBackgroundPan = (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (viewportFrameRef.current !== null) updateViewport(liveViewportRef.current);
    if (event.target !== event.target.getStage()) return;
    backgroundPanMovedRef.current = false;
    const pointer = getStagePointer(event);
    panOriginRef.current = pointer;
    panPointerRef.current = pointer;
  };

  const continueBackgroundPan = (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    const previousPointer = panPointerRef.current;
    const origin = panOriginRef.current;
    if (!previousPointer || !origin) return;

    const nextPointer = getStagePointer(event);
    if (!backgroundPanMovedRef.current) {
      const distance = Math.hypot(nextPointer.x - origin.x, nextPointer.y - origin.y);
      if (distance < GESTURE_THRESHOLD) {
        panPointerRef.current = nextPointer;
        return;
      }

      backgroundPanMovedRef.current = true;
      panPointerRef.current = origin;
    }

    const panPointer = panPointerRef.current;
    if (!panPointer) return;

    const panned = viewportModel.panBy(liveViewportRef.current, {
      x: nextPointer.x - panPointer.x,
      y: nextPointer.y - panPointer.y,
    });
    const nextViewport = viewportModel.createState({
      ...panned,
      offsetX: clampViewportOffset(panned.offsetX, worldWidth * panned.scale, stageWidth),
      offsetY: clampViewportOffset(panned.offsetY, worldHeight * panned.scale, stageHeight),
    });
    // Move immediately; refresh culled content at most once per frame.
    scheduleViewport(nextViewport);
    applyStageTranslation(event, nextViewport);
    if (worldOverlayRef.current) {
      worldOverlayRef.current.style.transform = `translate(${nextViewport.offsetX}px, ${nextViewport.offsetY}px) scale(${nextViewport.scale})`;
    }
    syncScrollPosition(nextViewport);
    panPointerRef.current = nextPointer;
  };

  const commitBackgroundPan = () => {
    if (!backgroundPanMovedRef.current) return;
    updateViewport(liveViewportRef.current);
  };

  const stopBackgroundPan = () => {
    commitBackgroundPan();
    panPointerRef.current = null;
    panOriginRef.current = null;
  };

  const cancelBackgroundPan = () => {
    commitBackgroundPan();
    panPointerRef.current = null;
    panOriginRef.current = null;
    backgroundPanMovedRef.current = false;
  };

  const cancelProductDrag = () => {
    const activeDrag = activeProductDragRef.current;
    if (!activeDrag || activeDrag.completed) return;

    activeDrag.completed = true;
    activeDrag.cancelVisual();
    activeProductDragRef.current = null;
    setProductDragPreview(null);
    setDropHintIfChanged(null);
    setDragSimulationIfChanged(null);
  };

  const cancelShelfDrag = () => {
    for (const activeDrag of activeShelfDragRef.current.values()) {
      if (activeDrag.completed) continue;
      activeDrag.completed = true;
      activeDrag.restoreVisual();
      activeDrag.stopDrag();
    }
    activeShelfDragRef.current.clear();
  };

  const cancelPlacementDrag = () => {
    const activeDrag = activePlacementDragRef.current;
    if (!activeDrag || activeDrag.completed) return;
    activeDrag.completed = true;
    activeDrag.restoreVisual();
    activePlacementDragRef.current = null;
  };

  const cancelContainerInteractions = () => {
    const activeDrag = activeContainerDragRef.current;
    if (activeDrag && !activeDrag.completed) {
      activeDrag.completed = true;
      activeDrag.restoreVisual();
    }
    activeContainerDragRef.current = null;

    const activeResize = activeContainerResizeRef.current;
    if (activeResize && !activeResize.completed) {
      activeResize.completed = true;
      activeResize.restoreVisual();
    }
    activeContainerResizeRef.current = null;
  };

  const cancelInteractions = () => {
    cancelBackgroundPan();
    cancelProductDrag();
    cancelShelfDrag();
    cancelPlacementDrag();
    cancelContainerInteractions();
  };

  const cancelOnEscape = useStableEvent((event: globalThis.KeyboardEvent) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    cancelInteractions();
  });
  const dragInProgress = productDragPreview !== null || shelfDragPreview !== null;
  useEffect(() => {
    if (!dragInProgress) return;
    window.addEventListener("keydown", cancelOnEscape);
    return () => window.removeEventListener("keydown", cancelOnEscape);
  }, [cancelOnEscape, dragInProgress]);

  const handleWheel = (event: KonvaEventObject<WheelEvent>) => {
    const intent = resolveViewportWheelIntent(event.evt);
    if (!intent) return;
    event.evt.preventDefault();
    if (intent.type === "pan" && worldWidth * liveViewportRef.current.scale <= stageWidth) return;
    const animation = viewportAnimationRef.current;
    const target = animation?.kind === intent.type ? animation.target : liveViewportRef.current;
    animateViewport(
      intent.type === "pan"
        ? viewportModel.panBy(target, intent.delta)
        : viewportModel.zoomBy(target, intent.factor, getStagePointer(event)),
      intent.type,
    );
  };

  const canMove = policy.canMove ?? true;

  const canSelectShelf = (shelfId: string) =>
    policy.canSelect?.({ id: shelfId, kind: "shelf" }) ?? true;

  const canSelectContainer = (containerId: string) =>
    policy.canSelect?.({ id: containerId, kind: "container" }) ?? true;

  const handleShelfSelection = (
    shelfId: string,
    event: { ctrlKey?: boolean; metaKey?: boolean },
  ) => {
    if (!canSelectShelf(shelfId)) return;
    updateSelection(selectShelf(scene, activeSelection, shelfId, event));
  };

  const submitShelfOperation = (
    operation: ShelfPlacementOperation | ShelfResizeOperation,
    shelfSelection: PlanogramSelection,
    restoreVisual: () => void,
  ) => {
    const result = policy.execute({ operation, pog, selection: shelfSelection });
    emitMoveResult(operation, result);
    if (result.status !== "committed") {
      restoreVisual();
      return;
    }
    setShelfDragPreview(null);
    updateSelection(result.selection);
    onCommit?.({ operation, pog: result.pog, selection: result.selection });
  };

  const handleShelfDragStart = (shelfId: string, event: KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;
    if (
      !canSelectShelf(shelfId) ||
      (activeSelection.kind === "shelf" && activeSelection.ids.length > 1)
    ) {
      event.target.stopDrag();
      return;
    }
    const shelfSelection =
      activeSelection.kind === "shelf" && activeSelection.ids.includes(shelfId)
        ? activeSelection
        : selectShelf(scene, activeSelection, shelfId);
    const domainShelf = pog.getShelf(shelfId);
    if (!domainShelf) {
      event.target.stopDrag();
      return;
    }
    updateSelection(shelfSelection);
    const startPointer = getStagePointer(event);
    const originalPosition = event.target.position();
    const restoreVisual = () => {
      event.target.position(originalPosition);
      setShelfDragPreview(null);
    };
    activeShelfDragRef.current.set(shelfId, {
      completed: false,
      originalPosition,
      restoreVisual,
      stopDrag: () => event.target.stopDrag(),
      selection: shelfSelection,
      shelfId,
      shelfIds: shelfSelection.ids,
      startWorldY: viewportModel.stageToWorld(activeViewport, startPointer).y,
    });
    setShelfDragPreview({ activeShelfId: shelfId, deltaY: 0, shelfIds: shelfSelection.ids });
  };

  const handleShelfDragMove = (shelfId: string, event: KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;
    const drag = activeShelfDragRef.current.get(shelfId);
    const domainShelf = pog.getShelf(shelfId);
    if (!drag || drag.completed || !domainShelf) return;

    const pointerWorldY = viewportModel.stageToWorld(activeViewport, getStagePointer(event)).y;
    const movingShelves = drag.shelfIds
      .map((id) => pog.getShelf(id))
      .filter((candidate) => candidate !== null);
    const motion = resolveShelfDragMotion(
      domainShelf,
      movingShelves,
      drag.startWorldY,
      pointerWorldY,
    );
    event.target.position({
      x: drag.originalPosition.x,
      y: drag.originalPosition.y + motion.deltaY,
    });
    setShelfDragPreviewIfChanged({
      activeShelfId: shelfId,
      deltaY: motion.deltaY,
      shelfIds: drag.shelfIds,
    });
  };

  const handleShelfDragEnd = (
    shelf: PlanogramSceneShelf | PlanogramSceneFixture,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const drag = activeShelfDragRef.current.get(shelf.id);
    if (!drag || drag.completed || drag.shelfId !== shelf.id) return;
    drag.completed = true;
    activeShelfDragRef.current.delete(shelf.id);
    const domainShelf = pog.getShelf(shelf.id);
    if (!domainShelf) {
      drag.restoreVisual();
      return;
    }
    const pointer = getStagePointer(event);
    const pointerWorldY = viewportModel.stageToWorld(activeViewport, pointer).y;
    const movingShelves = drag.shelfIds
      .map((id) => pog.getShelf(id))
      .filter((candidate) => candidate !== null);
    const motion = resolveShelfDragMotion(
      domainShelf,
      movingShelves,
      drag.startWorldY,
      pointerWorldY,
    );
    event.target.position({
      x: drag.originalPosition.x,
      y: drag.originalPosition.y + motion.deltaY,
    });
    const operation = createShelfPlacementOperation({
      activeShelfId: shelf.id,
      bayId: domainShelf.bay.uniqueId,
      notchNo: motion.targetNotch,
      shelfIds: drag.shelfIds,
    });
    submitShelfOperation(operation, drag.selection, drag.restoreVisual);
  };

  const getContainerSnappables = (containerId: string) => [
    ...getSnappableFixtures(pog.planogram),
    ...pog.containers
      .filter((candidate) => candidate.uniqueId !== containerId)
      .map((candidate) => ({
        xLeft: candidate.xLeft,
        xRight: candidate.xRight,
        yBottom: candidate.yBottom,
        yTop: candidate.yTop,
      })),
  ];

  const getContainerDragSelection = (containerId: string) =>
    activeSelection.kind === "container" && activeSelection.ids.includes(containerId)
      ? activeSelection
      : selectContainer(scene, activeSelection, containerId);

  const submitContainerOperation = (
    operation: ContainerMoveOperation | ContainerResizeOperation,
    containerSelection: PlanogramSelection,
    restoreVisual: () => void,
  ) => {
    const result = policy.execute({ operation, pog, selection: containerSelection });
    emitMoveResult(operation, result);
    restoreVisual();
    if (result.status !== "committed") return;
    updateSelection(result.selection);
    onCommit?.({ operation, pog: result.pog, selection: result.selection });
  };

  const handleContainerDragStart = (containerId: string, event: KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;
    if (!canSelectContainer(containerId)) {
      event.target.stopDrag();
      return;
    }
    const containerSelection = getContainerDragSelection(containerId);
    updateSelection(containerSelection);
    const dragNode = event.target;
    const originalPosition = dragNode.position();
    activeContainerDragRef.current = {
      completed: false,
      containerId,
      restoreVisual: () => dragNode.position(originalPosition),
      selection: containerSelection,
      startWorld: viewportModel.stageToWorld(activeViewport, getStagePointer(event)),
    };
  };

  const handleContainerDragEnd = (
    container: PlanogramSceneContainer,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const drag = activeContainerDragRef.current;
    if (!drag || drag.completed || drag.containerId !== container.id) return;
    drag.completed = true;
    activeContainerDragRef.current = null;
    const domainContainer = pog.containers.find((candidate) => candidate.uniqueId === container.id);
    if (!domainContainer) {
      drag.restoreVisual();
      return;
    }
    const endWorld = viewportModel.stageToWorld(activeViewport, getStagePointer(event));
    const deltaX = splitOffsets.toPogX(endWorld.x) - splitOffsets.toPogX(drag.startWorld.x);
    const deltaDomainY = drag.startWorld.y - endWorld.y;
    const bounds: ContainerBounds = moveCoordinatesChecker({
      bounds: getOuterBounds(pog.planogram),
      scaleX: activeViewport.scale,
      scaleY: activeViewport.scale,
      snappables: getContainerSnappables(container.id),
      xLeft: domainContainer.xLeft + deltaX,
      xRight: domainContainer.xRight + deltaX,
      yBottom: domainContainer.yBottom + deltaDomainY,
      yTop: domainContainer.yTop + deltaDomainY,
    });
    submitContainerOperation(
      createContainerMoveOperation({ bounds, containerId: container.id }),
      drag.selection,
      drag.restoreVisual,
    );
  };

  const handleContainerResizeStart = (
    containerId: string,
    side: ResizeSide,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    if (!canSelectContainer(containerId)) {
      event.target.stopDrag();
      return;
    }
    const containerSelection = getContainerDragSelection(containerId);
    updateSelection(containerSelection);
    const dragNode = event.target;
    const originalPosition = dragNode.position();
    activeContainerResizeRef.current = {
      completed: false,
      containerId,
      restoreVisual: () => dragNode.position(originalPosition),
      selection: containerSelection,
      side,
    };
  };

  const handleContainerResizeEnd = (
    container: PlanogramSceneContainer,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const resize = activeContainerResizeRef.current;
    if (!resize || resize.completed || resize.containerId !== container.id) return;
    resize.completed = true;
    activeContainerResizeRef.current = null;
    const domainContainer = pog.containers.find((candidate) => candidate.uniqueId === container.id);
    if (!domainContainer) {
      resize.restoreVisual();
      return;
    }
    const endWorld = viewportModel.stageToWorld(activeViewport, getStagePointer(event));
    const pointerDomainX = splitOffsets.toPogX(endWorld.x);
    const pointerDomainY = scene.height - endWorld.y;
    const bounds: ContainerBounds = resizeCoordinatesChecker({
      bounds: getOuterBounds(pog.planogram),
      scaleX: activeViewport.scale,
      scaleY: activeViewport.scale,
      side: resize.side,
      snappables: getContainerSnappables(container.id),
      xLeft: resizesLeft(resize.side) ? pointerDomainX : domainContainer.xLeft,
      xRight: resizesRight(resize.side) ? pointerDomainX : domainContainer.xRight,
      yBottom: resizesBottom(resize.side) ? pointerDomainY : domainContainer.yBottom,
      yTop: resizesTop(resize.side) ? pointerDomainY : domainContainer.yTop,
    });
    submitContainerOperation(
      createContainerResizeOperation({ bounds, containerId: container.id }),
      resize.selection,
      resize.restoreVisual,
    );
  };

  const canSelectPegboard = (pegboardId: string) =>
    policy.canSelect?.({ id: pegboardId, kind: "pegboard" }) ?? true;

  const canSelectPlacement = (placementId: string) =>
    policy.canSelect?.({ id: placementId, kind: "placement" }) ?? true;

  const canSelectProduct = (product: PlanogramSceneProduct) => {
    const entity = getProductEntity(product);
    return policy.canSelect?.(entity) ?? true;
  };

  const handleSelection = (
    product: PlanogramSceneProduct,
    event: { ctrlKey?: boolean; metaKey?: boolean },
  ) => {
    if (!canSelectProduct(product)) return;
    updateSelection(selectProduct(scene, activeSelection, product.id, event));
  };

  const activateContainer = (containerId: string) => {
    if (!canSelectContainer(containerId)) return;
    const container = scene.containers.find((candidate) => candidate.id === containerId);
    if (!container) return;
    const nextSelection = selectContainer(scene, activeSelection, containerId);
    updateSelection(nextSelection);
    onActivate?.(getContainerEntity(container), nextSelection);
  };

  const activatePegboard = (pegboardId: string) => {
    if (!canSelectPegboard(pegboardId)) return;
    const pegboard = selectablePegboards.find((candidate) => candidate.id === pegboardId);
    if (!pegboard) return;
    const nextSelection = selectPegboard(scene, activeSelection, pegboardId);
    updateSelection(nextSelection);
    onActivate?.(getPegboardEntity(pegboard), nextSelection);
  };

  const activatePlacement = (placement: PlanogramScenePlacement) => {
    if (!canSelectPlacement(placement.id)) return;
    const nextSelection = selectPlacement(scene, activeSelection, placement.id);
    updateSelection(nextSelection);
    onActivate?.(getPlacementEntity(placement), nextSelection);
  };

  const activateProduct = (product: PlanogramSceneProduct) => {
    if (!canSelectProduct(product)) return;
    const nextSelection = selectProduct(scene, activeSelection, product.id);
    updateSelection(nextSelection);
    onActivate?.(getProductEntity(product), nextSelection);
  };

  const activateShelf = (shelfId: string) => {
    if (!canSelectShelf(shelfId)) return;
    const nextSelection = selectShelf(scene, activeSelection, shelfId);
    updateSelection(nextSelection);
    onActivate?.({ id: shelfId, kind: "shelf" }, nextSelection);
  };

  const getRelativeMove = (
    product: PlanogramSceneProduct,
    direction: RelativeMoveDirection,
  ): {
    capability: ReturnType<PlanogramPolicy["evaluate"]>;
    operation: ItemPlacementOperation;
    selection: PlanogramSelection;
  } | null => {
    if (!canSelectProduct(product)) return null;

    const moveSelection: PlanogramSelection = selectedProductIds.has(product.id)
      ? activeSelection
      : {
          anchorId: product.id,
          focusedId: product.id,
          ids: [product.id],
          kind: "product",
        };
    const targetProduct = relativeMoveTargets.get(product.id)?.[direction];
    if (!targetProduct) return null;

    const operation = createItemPlacementOperation({
      activeItemId: product.id,
      intent: "move",
      side: direction,
      sourceItemIds: moveSelection.ids,
      targetItemId: targetProduct.id,
    });

    const capability = policy.evaluate(pog, operation);
    if (!capability.allowed) return null;

    return {
      capability,
      operation,
      selection: moveSelection,
    };
  };

  const handleFocus = (product: PlanogramSceneProduct) => {
    updateSelection(focusProduct(scene, activeSelection, product.id));
  };

  const handleBackgroundSelection = (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (event.target !== event.target.getStage()) return;

    if (!backgroundPanMovedRef.current) {
      updateSelection(clearSelection());
    }
    backgroundPanMovedRef.current = false;
  };

  const handlePlacementDragStart = (
    placement: PlanogramScenePlacement,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    if (!canSelectPlacement(placement.id)) {
      event.target.stopDrag();
      return;
    }
    const dragSelection =
      activeSelection.kind === "placement" && activeSelection.ids.includes(placement.id)
        ? activeSelection
        : selectPlacement(scene, activeSelection, placement.id);
    const dragNode = event.target;
    activePlacementDragRef.current = {
      activePlacementId: placement.id,
      completed: false,
      placementIds: dragSelection.ids,
      restoreVisual: () => dragNode.position({ x: placement.x, y: placement.y }),
      selection: dragSelection,
    };
    updateSelection(dragSelection);
  };

  const handlePlacementDragEnd = (
    placement: PlanogramScenePlacement,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const activeDrag = activePlacementDragRef.current;
    if (!activeDrag || activeDrag.completed || activeDrag.activePlacementId !== placement.id)
      return;
    activeDrag.completed = true;
    activePlacementDragRef.current = null;
    activeDrag.restoreVisual();
    const target = getNearestPegboardHole(
      scene,
      viewportModel.stageToWorld(activeViewport, getStagePointer(event)),
      PEGBOARD_HOLE_SNAP_TOLERANCE_PX / activeViewport.scale,
    );
    if (!target) {
      emitMoveResult(null, {
        pog,
        reason: "missing-target",
        selection: activeDrag.selection,
        status: "rejected",
      });
      return;
    }
    const operation = createPegboardPlacementOperation({
      activePlacementId: placement.id,
      holeNoLeft: target.holeNoLeft,
      holeNoTop: target.holeNoTop,
      pegboardId: target.pegboardId,
      placementIds: activeDrag.placementIds,
    });
    const result = policy.execute({ operation, pog, selection: activeDrag.selection });
    emitMoveResult(operation, result);
    if (result.status !== "committed") return;
    updateSelection(result.selection);
    onCommit?.({ operation, pog: result.pog, selection: result.selection });
  };

  const handleProductDragStart = (
    product: PlanogramSceneProduct,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    if (!canSelectProduct(product)) {
      event.target.stopDrag();
      return;
    }

    const dragSelection = getDragSelection(scene, activeSelection, product.id);
    const selectedIds = new Set(dragSelection.ids);
    const itemIds = scene.products
      .filter((candidate) => selectedIds.has(candidate.id))
      .map((candidate) => candidate.id);
    const dragNode = event.target;
    activeProductDragRef.current = {
      activeItemId: product.id,
      cancelVisual: () => {
        dragNode.stopDrag();
        dragNode.position({ x: product.x, y: product.y });
      },
      completed: false,
      intent: event.evt?.ctrlKey || event.evt?.metaKey ? "copy" : "move",
      itemIds,
      restoreVisual: () => dragNode.position({ x: product.x, y: product.y }),
      selection: dragSelection,
    };
    setProductDragPreview({ activeItemId: product.id, itemIds });

    updateSelection(dragSelection);
  };

  const emitMoveResult = (
    operation: Parameters<NonNullable<KonvaPlanogramProps["onMoveResult"]>>[0]["operation"],
    result: PlanogramMoveResult,
  ) => {
    onMoveResult?.({ operation, result });
  };

  const submitMoveOperation = (
    operation: ItemPlacementOperation | ItemRangeOperation,
    moveSelection: PlanogramSelection,
  ) => {
    const result = policy.execute({
      operation,
      pog,
      selection: moveSelection,
    });

    emitMoveResult(operation, result);

    if (result.status !== "committed") return;

    updateSelection(result.selection);
    onCommit?.({
      operation,
      pog: result.pog,
      selection: result.selection,
    });
  };

  const handleProductDragEnd = (
    product: PlanogramSceneProduct,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const activeDrag = activeProductDragRef.current;

    if (!activeDrag || activeDrag.activeItemId !== product.id || activeDrag.completed) {
      return;
    }

    activeDrag.completed = true;
    activeDrag.restoreVisual();
    activeProductDragRef.current = null;
    setProductDragPreview(null);
    setDropHintIfChanged(null);
    setDragSimulationIfChanged(null);

    const worldPoint = viewportModel.stageToWorld(activeViewport, getStagePointer(event));
    const targetEntity = hitTestProduct(scene, worldPoint, new Set(activeDrag.itemIds));

    if (!targetEntity) {
      const shelf =
        activeDrag.intent === "move"
          ? hitTestOtherShelfRow(scene, worldPoint, activeDrag.itemIds)
          : null;
      if (shelf) {
        submitMoveOperation(
          createItemRangeOperation({
            activeItemId: product.id,
            sourceItemIds: activeDrag.itemIds,
            target: { kind: "shelf", shelfId: shelf.id },
          }),
          activeDrag.selection,
        );
        return;
      }
      const result: PlanogramMoveResult = {
        pog,
        reason: "missing-target",
        selection: activeDrag.selection,
        status: "rejected",
      };
      emitMoveResult(null, result);
      return;
    }

    const targetProduct = scene.products.find((candidate) => candidate.id === targetEntity.id);
    if (!targetProduct) return;

    const operation = createItemPlacementOperation({
      activeItemId: product.id,
      // Konva dragmove has no evt; reading altKey off it crashed the drag layer.
      intent: activeDrag.intent,
      side: getInsertionSide(targetProduct, worldPoint),
      sourceItemIds: activeDrag.itemIds,
      targetItemId: targetProduct.id,
    });
    submitMoveOperation(operation, activeDrag.selection);
  };

  const handleRelativeMove = (product: PlanogramSceneProduct, direction: RelativeMoveDirection) => {
    const move = getRelativeMove(product, direction);
    if (!move) return;

    updateSelection(move.selection);
    submitMoveOperation(move.operation, move.selection);
  };

  const focusAdjacentProduct = (product: PlanogramSceneProduct, direction: "previous" | "next") => {
    const step = direction === "previous" ? -1 : 1;
    const productIndex = scene.products.findIndex((candidate) => candidate.id === product.id);

    for (
      let index = productIndex + step;
      index >= 0 && index < scene.products.length;
      index += step
    ) {
      const candidate = scene.products[index];
      const button = candidate && productButtonRefs.current.get(candidate.id);
      if (candidate && button && !button.disabled) {
        button.focus();
        return;
      }
    }
  };

  const focusAdjacentEntity = (
    kind: "pegboard" | "placement",
    id: string,
    direction: "previous" | "next",
  ) => {
    const entities = kind === "pegboard" ? selectablePegboards : scenePlacements;
    const refs = kind === "pegboard" ? pegboardButtonRefs.current : placementButtonRefs.current;
    const step = direction === "previous" ? -1 : 1;
    const entityIndex = entities.findIndex((candidate) => candidate.id === id);

    for (let index = entityIndex + step; index >= 0 && index < entities.length; index += step) {
      const candidate = entities[index];
      const button = candidate && refs.get(candidate.id);
      if (candidate && button && !button.disabled) {
        button.focus();
        return;
      }
    }
  };

  const handleProductKeyDown = (
    product: PlanogramSceneProduct,
    event: KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      activateProduct(product);
      return;
    }

    if (event.key === " " || event.key === "Space") {
      event.preventDefault();
      handleSelection(product, event);
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const direction = event.key === "ArrowLeft" ? "left" : "right";
      const move = getRelativeMove(product, direction);
      if (move) {
        handleRelativeMove(product, direction);
      } else {
        focusAdjacentProduct(product, direction === "left" ? "previous" : "next");
      }
      return;
    }

    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      focusAdjacentProduct(product, event.key === "ArrowUp" ? "previous" : "next");
    }
  };

  const handleSemanticEntityKeyDown = (
    kind: "pegboard" | "placement",
    id: string,
    event: KeyboardEvent<HTMLButtonElement>,
  ) => {
    const entity =
      kind === "pegboard"
        ? selectablePegboards.find((candidate) => candidate.id === id)
        : scenePlacements.find((candidate) => candidate.id === id);
    if (!entity) return;

    if (event.key === "Enter") {
      event.preventDefault();
      if (kind === "pegboard" ? !canSelectPegboard(id) : !canSelectPlacement(id)) return;
      const nextSelection =
        activeSelection.kind === kind && activeSelection.ids.includes(id)
          ? { ...activeSelection, focusedId: id }
          : kind === "pegboard"
            ? selectPegboard(scene, activeSelection, id)
            : selectPlacement(scene, activeSelection, id);
      updateSelection(nextSelection);
      onActivate?.({ id, kind }, nextSelection);
      return;
    }

    if (event.key === " " || event.key === "Space") {
      event.preventDefault();
      const nextSelection =
        kind === "pegboard"
          ? selectPegboard(scene, activeSelection, id, event)
          : selectPlacement(scene, activeSelection, id, event);
      updateSelection(nextSelection);
      return;
    }

    if (
      event.key === "ArrowLeft" ||
      event.key === "ArrowUp" ||
      event.key === "ArrowRight" ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault();
      focusAdjacentEntity(
        kind,
        id,
        event.key === "ArrowLeft" || event.key === "ArrowUp" ? "previous" : "next",
      );
    }
  };

  const handlePlacementActivation = (
    placement: PlanogramScenePlacement,
    event: KonvaEventObject<MouseEvent | TouchEvent>,
  ) => {
    event.cancelBubble = true;
    activatePlacement(placement);
  };

  const handleProductActivation = (
    product: PlanogramSceneProduct,
    event: KonvaEventObject<MouseEvent | TouchEvent>,
  ) => {
    event.cancelBubble = true;
    activateProduct(product);
  };

  const emitContextRequest = (
    entity: PlanogramEntityRef,
    nextSelection: PlanogramSelection,
    position?: PlanogramContextRequest["position"],
  ) => {
    updateSelection(nextSelection);
    const request: PlanogramContextRequest = position
      ? { entity, position, selection: nextSelection }
      : { entity, selection: nextSelection };
    onContextRequest?.(request);
  };

  const getContextPosition = (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    const inputEvent = event.evt;
    if ("clientX" in inputEvent && "clientY" in inputEvent) {
      return { clientX: inputEvent.clientX, clientY: inputEvent.clientY };
    }

    const touch = inputEvent.touches[0] ?? inputEvent.changedTouches[0];
    return touch ? { clientX: touch.clientX, clientY: touch.clientY } : undefined;
  };

  const requestContext = (
    event: KonvaEventObject<MouseEvent | TouchEvent>,
    entity: PlanogramEntityRef,
    nextSelection: PlanogramSelection,
  ) => {
    event.cancelBubble = true;
    event.evt.preventDefault();
    emitContextRequest(entity, nextSelection, getContextPosition(event));
  };

  const handleEntityClick = useStableEvent((event: KonvaEventObject<MouseEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    event.cancelBubble = true;

    switch (entity.kind) {
      case "product": {
        const product = scene.products.find((candidate) => candidate.id === entity.id);
        if (product) handleSelection(product, event.evt);
        return;
      }
      case "placement":
        if (canSelectPlacement(entity.id)) {
          updateSelection(selectPlacement(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "pegboard":
        if (canSelectPegboard(entity.id)) {
          updateSelection(selectPegboard(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "container":
        if (canSelectContainer(entity.id)) {
          updateSelection(selectContainer(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "shelf":
        handleShelfSelection(entity.id, event.evt);
        return;
      case "bay":
        return;
    }
  });

  const handleEntityContextMenu = useStableEvent((event: KonvaEventObject<PointerEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;

    if (entity.kind === "product") {
      const product = scene.products.find((candidate) => candidate.id === entity.id);
      if (!product || !canSelectProduct(product)) return;
      const nextSelection =
        activeSelection.kind === "product" && activeSelection.ids.includes(entity.id)
          ? { ...activeSelection, focusedId: entity.id }
          : selectProduct(scene, activeSelection, entity.id);
      requestContext(event, entity, nextSelection);
      return;
    }
    if (entity.kind === "placement") {
      if (canSelectPlacement(entity.id)) {
        requestContext(event, entity, selectPlacement(scene, activeSelection, entity.id));
      }
      return;
    }
    if (entity.kind === "pegboard") {
      if (canSelectPegboard(entity.id)) {
        requestContext(event, entity, selectPegboard(scene, activeSelection, entity.id));
      }
      return;
    }
    if (entity.kind === "container") {
      if (canSelectContainer(entity.id)) {
        const nextSelection =
          activeSelection.kind === "container" && activeSelection.ids.includes(entity.id)
            ? { ...activeSelection, focusedId: entity.id }
            : selectContainer(scene, activeSelection, entity.id);
        requestContext(event, entity, nextSelection);
      }
      return;
    }
    if (entity.kind === "shelf" && canSelectShelf(entity.id)) {
      const nextSelection =
        activeSelection.kind === "shelf" && activeSelection.ids.includes(entity.id)
          ? { ...activeSelection, focusedId: entity.id }
          : selectShelf(scene, activeSelection, entity.id);
      requestContext(event, entity, nextSelection);
    }
  });

  const handleStageContextMenu = useStableEvent((event: KonvaEventObject<PointerEvent>) => {
    const pointer = getStagePointer(event);
    const worldPoint = viewportModel.stageToWorld(activeViewport, pointer);
    const bay = scene.bays.find(
      (candidate) =>
        worldPoint.x >= candidate.x &&
        worldPoint.x <= candidate.x + candidate.width &&
        worldPoint.y >= candidate.y &&
        worldPoint.y <= candidate.y + candidate.height,
    );
    if (!bay) return;

    event.cancelBubble = true;
    event.evt.preventDefault();
    emitContextRequest({ kind: "bay", id: bay.id }, activeSelection, getContextPosition(event));
  });

  const handleEntityDoubleClick = useStableEvent((event: KonvaEventObject<MouseEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    event.cancelBubble = true;

    switch (entity.kind) {
      case "product": {
        const product = scene.products.find((candidate) => candidate.id === entity.id);
        if (product) handleProductActivation(product, event);
        return;
      }
      case "placement": {
        const placement = scenePlacements.find((candidate) => candidate.id === entity.id);
        if (placement) handlePlacementActivation(placement, event);
        return;
      }
      case "pegboard":
        activatePegboard(entity.id);
        return;
      case "container":
        activateContainer(entity.id);
        return;
      case "shelf":
        activateShelf(entity.id);
        return;
      case "bay":
        return;
    }
  });

  const handleEntityDoubleTap = useStableEvent((event: KonvaEventObject<TouchEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    event.cancelBubble = true;

    switch (entity.kind) {
      case "product": {
        const product = scene.products.find((candidate) => candidate.id === entity.id);
        if (product) handleProductActivation(product, event);
        return;
      }
      case "placement": {
        const placement = scenePlacements.find((candidate) => candidate.id === entity.id);
        if (placement) handlePlacementActivation(placement, event);
        return;
      }
      case "pegboard":
        activatePegboard(entity.id);
        return;
      case "container":
        activateContainer(entity.id);
        return;
      case "shelf":
        activateShelf(entity.id);
        return;
      case "bay":
        return;
    }
  });

  const handleEntityTap = useStableEvent((event: KonvaEventObject<TouchEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    event.cancelBubble = true;

    switch (entity.kind) {
      case "product": {
        const product = scene.products.find((candidate) => candidate.id === entity.id);
        if (product) handleSelection(product, event.evt);
        return;
      }
      case "placement":
        if (canSelectPlacement(entity.id)) {
          updateSelection(selectPlacement(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "pegboard":
        if (canSelectPegboard(entity.id)) {
          updateSelection(selectPegboard(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "container":
        if (canSelectContainer(entity.id)) {
          updateSelection(selectContainer(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "shelf":
        handleShelfSelection(entity.id, event.evt);
        return;
      case "bay":
        return;
    }
  });

  const handleEntityMouseEnter = useStableEvent((event?: KonvaEventObject<MouseEvent>) => {
    const entity = event && getEntityFromEvent(event);
    if (entity) setHoveredEntity(entity);
  });

  const handleEntityMouseLeave = useStableEvent((event?: KonvaEventObject<MouseEvent>) => {
    const entity = event && getEntityFromEvent(event);
    if (entity) clearHoveredEntity(entity.id);
  });

  const handleEntityPointerDown = useStableEvent(
    (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (viewportFrameRef.current !== null) updateViewport(liveViewportRef.current);
      backgroundPanMovedRef.current = false;
      event.cancelBubble = true;
    },
  );

  const handleEntityDragStart = useStableEvent((event: KonvaEventObject<DragEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    if (entity.kind === "product") {
      const product = scene.products.find((candidate) => candidate.id === entity.id);
      if (product) handleProductDragStart(product, event);
      return;
    }
    if (entity.kind === "placement") {
      const placement = scenePlacements.find((candidate) => candidate.id === entity.id);
      if (placement) handlePlacementDragStart(placement, event);
      return;
    }
    if (entity.kind === "container") {
      handleContainerDragStart(entity.id, event);
      return;
    }
    if (entity.kind === "shelf") handleShelfDragStart(entity.id, event);
  });

  const handleEntityDragEnd = useStableEvent((event: KonvaEventObject<DragEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    if (entity.kind === "product") {
      const product = scene.products.find((candidate) => candidate.id === entity.id);
      if (product) handleProductDragEnd(product, event);
      return;
    }
    if (entity.kind === "placement") {
      const placement = scenePlacements.find((candidate) => candidate.id === entity.id);
      if (placement) handlePlacementDragEnd(placement, event);
      return;
    }
    if (entity.kind === "container") {
      const container = scene.containers.find((candidate) => candidate.id === entity.id);
      if (container) handleContainerDragEnd(container, event);
      return;
    }
    if (entity.kind === "shelf") {
      const shelf = scene.shelves.find((candidate) => candidate.id === entity.id);
      const fixture = scene.fixtures.find((candidate) => candidate.id === entity.id);
      if (shelf) handleShelfDragEnd(shelf, event);
      else if (fixture) handleShelfDragEnd(fixture, event);
    }
  });

  const handleEntityDragMove = useStableEvent((event: KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;

    const entity = getEntityFromEvent(event);
    if (entity?.kind === "shelf") {
      handleShelfDragMove(entity.id, event);
      return;
    }

    const activeDrag = activeProductDragRef.current;
    if (!activeDrag || activeDrag.completed || entity?.kind !== "product") return;

    const worldPoint = viewportModel.stageToWorld(activeViewport, getStagePointer(event));
    const dragged = new Set(activeDrag.itemIds);
    const targetEntity = hitTestProduct(scene, worldPoint, dragged);
    const targetProduct = targetEntity
      ? scene.products.find((candidate) => candidate.id === targetEntity.id)
      : undefined;

    const targetShelf =
      !targetProduct && activeDrag.intent === "move"
        ? hitTestOtherShelfRow(scene, worldPoint, activeDrag.itemIds)
        : null;
    if (!targetProduct && !targetShelf) {
      setDropHintIfChanged(null);
      setDragSimulationIfChanged(null);
      return;
    }

    const side = targetProduct ? getInsertionSide(targetProduct, worldPoint) : "right";
    const targetId = targetProduct?.id ?? targetShelf?.id;
    const key = `${targetId}|${side}|${activeDrag.itemIds.join(",")}`;
    if (dragSimulation?.key === key) return;

    const operation = targetProduct
      ? createItemPlacementOperation({
          activeItemId: activeDrag.activeItemId,
          // Konva dragmove has no evt; reading altKey off it crashed the drag layer.
          intent: activeDrag.intent,
          side,
          sourceItemIds: activeDrag.itemIds,
          targetItemId: targetProduct.id,
        })
      : targetShelf
        ? createItemRangeOperation({
            activeItemId: activeDrag.activeItemId,
            sourceItemIds: activeDrag.itemIds,
            target: { kind: "shelf", shelfId: targetShelf.id },
          })
        : null;
    if (!operation || !targetId) return;
    const result = policy.execute({ operation, pog, selection: activeDrag.selection });

    if (result.status !== "committed") {
      setDragSimulationIfChanged(null);
      setDropHintIfChanged(null);
      return;
    }

    const simulated = sceneProjector.project(result.pog, resolvedView.productLabel);
    const landed = simulated.products.filter((candidate) => dragged.has(candidate.id));
    const hint: ProductDropHint | null = landed.length
      ? {
          caretX: Math.min(...landed.map((candidate) => candidate.x)),
          height: Math.max(...landed.map((candidate) => candidate.height)),
          targetId,
          width: landed.reduce((sum, candidate) => sum + candidate.width, 0),
          y: Math.min(...landed.map((candidate) => candidate.y)),
        }
      : null;

    setDragSimulationIfChanged({ hint, key, scene: simulated });
    setDropHintIfChanged(hint);
  });

  const renderAccessibleControls = useStableEvent(() => (
    <>
      <AccessibleControls aria-label={labels.containers} role="list">
        {scene.containers.map((container) => {
          const entity = getContainerEntity(container);
          const selected = selectedContainerIds.has(container.id);
          return (
            <AccessibleProductGroup key={container.id} role="listitem">
              <AccessibleProductButton
                aria-label={labels.selectContainer(container.label)}
                aria-pressed={selected}
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="container"
                disabled={!canSelectContainer(container.id)}
                onClick={(event) => {
                  if (canSelectContainer(container.id)) {
                    updateSelection(selectContainer(scene, activeSelection, container.id, event));
                  }
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectContainer(container.id)) {
                    emitContextRequest(
                      entity,
                      selectContainer(scene, activeSelection, container.id),
                    );
                  }
                }}
                onDoubleClick={() => {
                  activateContainer(container.id);
                }}
                onFocus={() =>
                  updateSelection(focusEntity(scene, activeSelection, "container", container.id))
                }
                data-planogram-focused={activeSelection.focusedId === container.id}
                tabIndex={0}
                type="button"
              >
                {container.label}
              </AccessibleProductButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.products} role="list">
        {scene.products.map((product) => {
          const entity = getProductEntity(product);
          const label = product.label || product.name || product.productCode || product.id;
          const productStatusLabel = product.isNewInsertedItem ? labels.newItemIndicator : null;
          const focused = activeSelection.focusedId === product.id;
          const leftMove = getRelativeMove(product, "left");
          const rightMove = getRelativeMove(product, "right");
          return (
            <AccessibleProductGroup key={product.id} role="listitem">
              <AccessibleProductButton
                aria-label={combineAccessibleLabels(
                  combineAccessibleLabels(labels.selectProduct(label), productStatusLabel),
                  product.frontOverhang?.description ?? null,
                )}
                aria-pressed={selectedProductIds.has(product.id)}
                aria-keyshortcuts="Enter Space ArrowLeft ArrowRight ArrowUp ArrowDown"
                data-planogram-entity-id={entity.id}
                data-planogram-status={product.isNewInsertedItem ? "new-item" : undefined}
                disabled={!canSelectProduct(product)}
                data-planogram-focused={focused}
                onClick={(event) => handleSelection(product, event)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  emitContextRequest(entity, selectProduct(scene, activeSelection, product.id));
                }}
                onFocus={() => handleFocus(product)}
                ref={(button) => {
                  if (button) productButtonRefs.current.set(product.id, button);
                  else productButtonRefs.current.delete(product.id);
                }}
                onDoubleClick={() => {
                  activateProduct(product);
                }}
                onKeyDown={(event) => handleProductKeyDown(product, event)}
                tabIndex={0}
                type="button"
              >
                {label}
              </AccessibleProductButton>
              <AccessibleMoveButton
                aria-label={labels.moveProductLeft(label)}
                data-planogram-entity-id={entity.id}
                data-planogram-operation="move-left"
                disabled={!leftMove}
                onClick={() => {
                  if (!leftMove) return;
                  updateSelection(leftMove.selection);
                  submitMoveOperation(leftMove.operation, leftMove.selection);
                }}
                type="button"
              >
                {labels.moveLeft}
              </AccessibleMoveButton>
              <AccessibleMoveButton
                aria-label={labels.moveProductRight(label)}
                data-planogram-entity-id={entity.id}
                data-planogram-operation="move-right"
                disabled={!rightMove}
                onClick={() => {
                  if (!rightMove) return;
                  updateSelection(rightMove.selection);
                  submitMoveOperation(rightMove.operation, rightMove.selection);
                }}
                type="button"
              >
                {labels.moveRight}
              </AccessibleMoveButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.pegboards} role="list">
        {selectablePegboards.map((pegboard) => {
          const entity = getPegboardEntity(pegboard);
          const selected = selectedPegboardIds.has(pegboard.id);
          return (
            <AccessibleProductGroup key={pegboard.id} role="listitem">
              <AccessibleProductButton
                aria-label={labels.selectPegboard(pegboard.id)}
                aria-pressed={selected}
                aria-keyshortcuts="Enter Space ArrowLeft ArrowRight ArrowUp ArrowDown"
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="pegboard"
                disabled={!canSelectPegboard(pegboard.id)}
                onClick={(event) => {
                  if (canSelectPegboard(pegboard.id)) {
                    updateSelection(selectPegboard(scene, activeSelection, pegboard.id, event));
                  }
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectPegboard(pegboard.id)) {
                    emitContextRequest(entity, selectPegboard(scene, activeSelection, pegboard.id));
                  }
                }}
                onDoubleClick={() => {
                  activatePegboard(pegboard.id);
                }}
                onFocus={() =>
                  updateSelection(focusEntity(scene, activeSelection, "pegboard", pegboard.id))
                }
                onKeyDown={(event) => handleSemanticEntityKeyDown("pegboard", pegboard.id, event)}
                ref={(button) => {
                  if (button) pegboardButtonRefs.current.set(pegboard.id, button);
                  else pegboardButtonRefs.current.delete(pegboard.id);
                }}
                data-planogram-focused={activeSelection.focusedId === pegboard.id}
                tabIndex={0}
                type="button"
              >
                {pegboard.id}
              </AccessibleProductButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.placements} role="list">
        {scenePlacements.map((placement) => {
          const entity = getPlacementEntity(placement);
          const selected = selectedPlacementIds.has(placement.id);
          const placementStatusLabel =
            placement.facingChangeColor && placement.facingChangeType
              ? labels.pegboardFacingChangeIndicator(placement.facingChangeType)
              : null;
          return (
            <AccessibleProductGroup key={placement.id} role="listitem">
              <AccessibleProductButton
                aria-label={combineAccessibleLabels(
                  labels.selectPlacement(placement.label),
                  placementStatusLabel,
                )}
                aria-pressed={selected}
                aria-keyshortcuts="Enter Space ArrowLeft ArrowRight ArrowUp ArrowDown"
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="placement"
                data-planogram-facing-change={placement.facingChangeType ?? undefined}
                disabled={!canSelectPlacement(placement.id)}
                onClick={(event) => {
                  if (canSelectPlacement(placement.id)) {
                    updateSelection(selectPlacement(scene, activeSelection, placement.id, event));
                  }
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectPlacement(placement.id)) {
                    emitContextRequest(
                      entity,
                      selectPlacement(scene, activeSelection, placement.id),
                    );
                  }
                }}
                onDoubleClick={() => {
                  activatePlacement(placement);
                }}
                onFocus={() =>
                  updateSelection(focusEntity(scene, activeSelection, "placement", placement.id))
                }
                onKeyDown={(event) => handleSemanticEntityKeyDown("placement", placement.id, event)}
                ref={(button) => {
                  if (button) placementButtonRefs.current.set(placement.id, button);
                  else placementButtonRefs.current.delete(placement.id);
                }}
                data-planogram-focused={activeSelection.focusedId === placement.id}
                tabIndex={0}
                type="button"
              >
                {placement.label}
              </AccessibleProductButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.shelves} role="list">
        {scene.shelves.map((shelf) => {
          const entity: PlanogramEntityRef = { id: shelf.id, kind: "shelf" };
          const selected = selectedShelfIds.has(shelf.id);
          const shelfSelection = () =>
            selectedShelfIds.has(shelf.id)
              ? activeSelection
              : selectShelf(scene, activeSelection, shelf.id);
          const resizeShelf = (width: number) => {
            const nextSelection = shelfSelection();
            updateSelection(nextSelection);
            submitShelfOperation(
              createShelfResizeOperation({
                activeShelfId: shelf.id,
                dimensions: { width },
                shelfIds: nextSelection.ids,
              }),
              nextSelection,
              () => undefined,
            );
          };
          return (
            <AccessibleProductGroup key={shelf.id} role="listitem">
              <AccessibleProductButton
                aria-label={combineAccessibleLabels(
                  labels.selectShelf(shelf.shelfNo),
                  shelf.frontOverhang?.description ?? null,
                )}
                aria-pressed={selected}
                data-planogram-entity-id={shelf.id}
                data-planogram-entity-kind="shelf"
                disabled={!canSelectShelf(shelf.id)}
                onClick={(event) => handleShelfSelection(shelf.id, event)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectShelf(shelf.id)) {
                    emitContextRequest(entity, selectShelf(scene, activeSelection, shelf.id));
                  }
                }}
                onDoubleClick={() => {
                  activateShelf(shelf.id);
                }}
                type="button"
              >
                {labels.shelf(shelf.shelfNo)}
              </AccessibleProductButton>
              <AccessibleResizeButton
                aria-label={labels.resizeShelfWider(shelf.shelfNo)}
                data-planogram-entity-id={shelf.id}
                data-planogram-operation="shelf-resize"
                disabled={!canMove || !canSelectShelf(shelf.id)}
                onClick={() => resizeShelf(shelf.width + 1)}
                type="button"
              >
                {labels.resizeShelfWiderText}
              </AccessibleResizeButton>
              <AccessibleResizeButton
                aria-label={labels.resizeShelfNarrower(shelf.shelfNo)}
                data-planogram-entity-id={shelf.id}
                data-planogram-operation="shelf-resize"
                disabled={!canMove || !canSelectShelf(shelf.id) || shelf.width <= 1}
                onClick={() => resizeShelf(Math.max(1, shelf.width - 1))}
                type="button"
              >
                {labels.resizeShelfNarrowerText}
              </AccessibleResizeButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.fixtures} role="list">
        {scene.fixtures.map((fixture) => {
          const entity: PlanogramEntityRef = { id: fixture.id, kind: "shelf" };
          const selected = selectedShelfIds.has(fixture.id);
          const fixtureSelection = () =>
            selectedShelfIds.has(fixture.id)
              ? activeSelection
              : selectShelf(scene, activeSelection, fixture.id);
          const resizeFixture = (width: number) => {
            const nextSelection = fixtureSelection();
            updateSelection(nextSelection);
            submitShelfOperation(
              createShelfResizeOperation({
                activeShelfId: fixture.id,
                dimensions: { width },
                shelfIds: nextSelection.ids,
              }),
              nextSelection,
              () => undefined,
            );
          };
          return (
            <AccessibleProductGroup key={fixture.id} role="listitem">
              <AccessibleProductButton
                aria-label={labels.selectFixture(fixture.fixtureType, fixture.shelfNo)}
                aria-pressed={selected}
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="fixture"
                disabled={!canSelectShelf(fixture.id)}
                onClick={(event) => handleShelfSelection(fixture.id, event)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectShelf(fixture.id)) {
                    emitContextRequest(entity, selectShelf(scene, activeSelection, fixture.id));
                  }
                }}
                onDoubleClick={() => {
                  activateShelf(fixture.id);
                }}
                type="button"
              >
                {labels.fixture(fixture.fixtureType, fixture.shelfNo)}
              </AccessibleProductButton>
              <AccessibleResizeButton
                aria-label={labels.resizeFixtureNarrower(fixture.fixtureType, fixture.shelfNo)}
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="fixture"
                data-planogram-operation="fixture-resize"
                disabled={!canMove || !canSelectShelf(fixture.id) || fixture.width <= 1}
                onClick={() => resizeFixture(Math.max(1, fixture.width - 1))}
                type="button"
              >
                {labels.resizeFixtureNarrowerText}
              </AccessibleResizeButton>
              <AccessibleResizeButton
                aria-label={labels.resizeFixtureWider(fixture.fixtureType, fixture.shelfNo)}
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="fixture"
                data-planogram-operation="fixture-resize"
                disabled={!canMove || !canSelectShelf(fixture.id)}
                onClick={() => resizeFixture(fixture.width + 1)}
                type="button"
              >
                {labels.resizeFixtureWiderText}
              </AccessibleResizeButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
    </>
  ));
  const accessibleControls = useMemo(
    () => renderAccessibleControls(),
    [
      renderAccessibleControls,
      scene,
      activeSelection,
      labels,
      policy,
      pog,
      onActivate,
      onCommit,
      onContextRequest,
      onMoveResult,
      onSelectionChange,
    ],
  );

  return (
    <CanvasRoot
      data-product-display-mode={resolvedView.productDisplayMode}
      data-product-label={resolvedView.productLabel}
      data-projection-count={projectionStats.projectionCount}
      data-testid="konva-planogram"
      ref={canvasRootRef}
      onScroll={handleViewportScroll}
    >
      <CanvasSurface
        $height={surfaceHeight}
        $width={surfaceWidth}
        data-testid="konva-planogram-scroll-surface"
      >
        <StageViewport
          $height={stageHeight}
          $width={stageWidth}
          data-testid="konva-planogram-stage-viewport"
          onPointerLeave={aisleBreakEditing ? () => setHoveredSeam(null) : undefined}
          onPointerMove={aisleBreakEditing ? handleSeamPointerMove : undefined}
        >
          <Stage
            height={stageHeight}
            ref={stageRef}
            onClick={handleBackgroundSelection}
            onContextMenu={handleStageContextMenu}
            onMouseDown={startBackgroundPan}
            onMouseLeave={() => {
              cancelInteractions();
              setHoveredEntityState(null);
            }}
            onMouseMove={continueBackgroundPan}
            onMouseUp={stopBackgroundPan}
            onTouchCancel={cancelInteractions}
            onTouchEnd={stopBackgroundPan}
            onTouchMove={continueBackgroundPan}
            onTouchStart={startBackgroundPan}
            onTap={handleBackgroundSelection}
            onWheel={handleWheel}
            scaleX={activeViewport.scale}
            scaleY={activeViewport.scale}
            width={stageWidth}
            x={activeViewport.offsetX}
            y={activeViewport.offsetY}
          >
            <Layer name="planogram-scene">
              <Group listening={false} name="planogram-structure">
                {scene.bays.filter(isInRenderWindow).map((bay) => (
                  <Group key={bay.id} listening={false} name={`bay-${bay.id}`}>
                    <Rect
                      fill={Color.sand50}
                      height={bay.height}
                      name={`bay-${bay.id}`}
                      stroke={Color.spaceGray}
                      width={bay.width}
                      x={bay.x}
                      y={bay.y}
                    />
                    {bay.baseHeight > 0 && (
                      <>
                        <Rect
                          data-base-height={bay.baseHeight}
                          fill={Color.sand300}
                          fillLinearGradientColorStops={[0, Color.sand300, 1, Color.sand500]}
                          fillLinearGradientEndPoint={{ x: 0, y: bay.baseHeight }}
                          fillLinearGradientStartPoint={{ x: 0, y: 0 }}
                          height={bay.baseHeight}
                          listening={false}
                          name={`bay-base-${bay.id}`}
                          opacity={0.7}
                          width={bay.width}
                          x={bay.x}
                          y={bay.y + bay.height - bay.baseHeight}
                        />
                        <Text
                          align="center"
                          fill={Color.sand700}
                          fontSize={10 / activeViewport.scale}
                          fontStyle="bold"
                          height={bay.baseHeight}
                          listening={false}
                          name={`bay-label-${bay.id}`}
                          text={labels.bay(bay.bayNo)}
                          verticalAlign="middle"
                          width={bay.width}
                          x={bay.x}
                          y={bay.y + bay.height - bay.baseHeight}
                        />
                      </>
                    )}
                    {Array.from({ length: bay.maxNotch }, (_, notchIndex) => {
                      const notchBottom = bay.notchOffset + notchIndex * bay.notchSpacing;
                      return (
                        <Rect
                          data-notch-index={notchIndex}
                          data-notch-position={notchBottom}
                          fill={Color.sand700}
                          height={1}
                          key={`${bay.id}:notch:${notchIndex}`}
                          listening={false}
                          name={`bay-notch-${bay.id}-${notchIndex}`}
                          width={bay.width}
                          x={bay.x}
                          y={bay.y + bay.height - notchBottom - 1}
                        />
                      );
                    })}
                  </Group>
                ))}
                {scene.splits.map((split) => {
                  const seamX = split.x + split.width / 2;
                  const scale = activeViewport.scale;
                  const caption = layoutAisleBreakCaption(split.width * scale);
                  return (
                    <Group key={split.id} listening={false} name={`split-${split.id}`}>
                      <Line
                        dash={[8 / activeViewport.scale, 6 / activeViewport.scale]}
                        listening={false}
                        name={`split-divider-${split.id}`}
                        points={[seamX, split.y, seamX, split.y + split.height]}
                        stroke={Color.spaceGray}
                        strokeWidth={2 / activeViewport.scale}
                      />
                      {/* Read down the right half of the gap, clear of the next bay's products;
                          left out when the gap is too narrow on screen to hold it. */}
                      {caption && (
                        <Text
                          align="center"
                          fill={Color.spaceGray}
                          fontSize={caption.fontSize / scale}
                          fontStyle="bold"
                          lineHeight={1}
                          listening={false}
                          name={`split-label-${split.id}`}
                          rotation={90}
                          text={labels.aisleBreak}
                          width={split.height}
                          // Turned a quarter clockwise, the text's top edge is its right edge.
                          x={seamX + (caption.centerOffset + caption.fontSize / 2) / scale}
                          y={split.y}
                        />
                      )}
                    </Group>
                  );
                })}
              </Group>
              {scenePegboards.length > 0 && (
                <Group name="planogram-pegboards">
                  {scenePegboards.filter(isInRenderWindow).map((pegboard) => {
                    const isInteractive = pegboard.source === "planogram";
                    return (
                      <Group
                        id={pegboard.id}
                        key={pegboard.id}
                        listening={isInteractive}
                        name={`pegboard-${pegboard.id}`}
                        onContextMenu={isInteractive ? handleEntityContextMenu : undefined}
                        onClick={isInteractive ? handleEntityClick : undefined}
                        onDblClick={isInteractive ? handleEntityDoubleClick : undefined}
                        onMouseEnter={isInteractive ? handleEntityMouseEnter : undefined}
                        onMouseLeave={isInteractive ? handleEntityMouseLeave : undefined}
                        onTap={isInteractive ? handleEntityTap : undefined}
                        y={getShelfDragOffset(pegboard.sourceShelfId)}
                      >
                        <Rect
                          fill={Color.gray400}
                          height={pegboard.height}
                          stroke={
                            selectedPegboardIds.has(pegboard.id) ? Color.primary : Color.spaceGray
                          }
                          strokeWidth={selectedPegboardIds.has(pegboard.id) ? 2 : 1}
                          width={pegboard.width}
                          x={pegboard.x}
                          y={pegboard.y}
                        />
                        {pegboard.holes.map((hole) => (
                          <Rect
                            fill={Color.textMain}
                            height={hole.height}
                            key={`${pegboard.id}:${hole.column}:${hole.row}`}
                            listening={false}
                            width={hole.width}
                            x={hole.x}
                            y={hole.y}
                          />
                        ))}
                      </Group>
                    );
                  })}
                </Group>
              )}
              {scene.containers.length > 0 && (
                <Group name="planogram-containers">
                  {scene.containers
                    .filter(
                      (container) =>
                        isInRenderWindow(container) ||
                        activeContainerDragRef.current?.containerId === container.id ||
                        activeContainerResizeRef.current?.containerId === container.id,
                    )
                    .map((container) => {
                      const selected = selectedContainerIds.has(container.id);
                      return (
                        <Group
                          draggable={canMove}
                          id={container.id}
                          key={container.id}
                          name={`container-${container.id}`}
                          onClick={handleEntityClick}
                          onContextMenu={handleEntityContextMenu}
                          onDblClick={handleEntityDoubleClick}
                          onDragEnd={canMove ? handleEntityDragEnd : undefined}
                          onDragMove={canMove ? handleEntityDragMove : undefined}
                          onDragStart={canMove ? handleEntityDragStart : undefined}
                          onMouseDown={handleEntityPointerDown}
                          onMouseEnter={handleEntityMouseEnter}
                          onMouseLeave={handleEntityMouseLeave}
                          onTap={handleEntityTap}
                          onTouchStart={handleEntityPointerDown}
                        >
                          <Rect
                            fill={container.color}
                            height={container.height}
                            stroke={selected ? Color.primary : Color.spaceGray}
                            strokeWidth={selected ? 2 : 1}
                            width={container.width}
                            x={container.x}
                            y={container.y}
                          />
                          <Text
                            align="center"
                            fill={Color.textMain}
                            height={container.height}
                            listening={false}
                            text={container.label}
                            verticalAlign="middle"
                            width={container.width}
                            x={container.x}
                            y={container.y}
                          />
                        </Group>
                      );
                    })}
                  {canMove &&
                    scene.containers
                      .filter((container) => selectedContainerIds.has(container.id))
                      .flatMap((container) => {
                        const handleSize = 8 / activeViewport.scale;
                        return CONTAINER_RESIZE_SIDES.map((side) => {
                          const position = getContainerResizeHandlePosition(
                            container,
                            side,
                            handleSize,
                          );
                          return (
                            <Rect
                              draggable
                              fill={Color.white}
                              height={handleSize}
                              id={container.id}
                              key={`${container.id}:resize:${side}`}
                              name={`container-resize-${side}-${container.id}`}
                              onDragEnd={(event) => handleContainerResizeEnd(container, event)}
                              onDragStart={(event) =>
                                handleContainerResizeStart(container.id, side, event)
                              }
                              onMouseDown={handleEntityPointerDown}
                              stroke={Color.primary}
                              strokeWidth={1}
                              width={handleSize}
                              x={position.x}
                              y={position.y}
                            />
                          );
                        });
                      })}
                </Group>
              )}
              <Group name="planogram-fixtures">
                {scene.shelves
                  .filter((shelf) => isInRenderWindow(shelf) || shelfDragPreviewIds.has(shelf.id))
                  .map((shelf) => (
                    <Rect
                      draggable={canMove}
                      fill={shelf.rackName ? RACK_BOARD_FILL : Color.forest950}
                      height={shelf.height}
                      id={shelf.id}
                      key={shelf.id}
                      name={`shelf-${shelf.id}`}
                      onContextMenu={handleEntityContextMenu}
                      onClick={handleEntityClick}
                      onDblClick={handleEntityDoubleClick}
                      onDragEnd={canMove ? handleEntityDragEnd : undefined}
                      onDragMove={canMove ? handleEntityDragMove : undefined}
                      onDragStart={canMove ? handleEntityDragStart : undefined}
                      onMouseEnter={handleEntityMouseEnter}
                      onMouseLeave={handleEntityMouseLeave}
                      onTap={handleEntityTap}
                      stroke={selectedShelfIds.has(shelf.id) ? Color.primary : Color.spaceGray}
                      strokeWidth={selectedShelfIds.has(shelf.id) ? 2 : 1}
                      width={shelf.boardWidth}
                      x={shelf.x + shelf.boardX}
                      y={
                        shelf.y +
                        (shelfDragPreview?.activeShelfId === shelf.id
                          ? 0
                          : getShelfDragOffset(shelf.id))
                      }
                    />
                  ))}
                {scene.fixtures
                  .filter(
                    (fixture) => isInRenderWindow(fixture) || shelfDragPreviewIds.has(fixture.id),
                  )
                  .map((fixture) => {
                    const selected = selectedShelfIds.has(fixture.id);
                    const isHangcell = fixture.fixtureType === "HANGCELL";
                    return (
                      <Rect
                        draggable={canMove}
                        fill={
                          isHangcell
                            ? selected
                              ? Color.pureYellow
                              : Color.forest950
                            : Color.gray400
                        }
                        height={isHangcell ? fixture.thickness : fixture.height}
                        id={fixture.id}
                        key={fixture.id}
                        name={`fixture-${fixture.fixtureType.toLowerCase()}-${fixture.id}`}
                        onContextMenu={handleEntityContextMenu}
                        onClick={handleEntityClick}
                        onDblClick={handleEntityDoubleClick}
                        onDragEnd={canMove ? handleEntityDragEnd : undefined}
                        onDragMove={canMove ? handleEntityDragMove : undefined}
                        onDragStart={
                          canMove ? (event) => handleShelfDragStart(fixture.id, event) : undefined
                        }
                        onMouseEnter={handleEntityMouseEnter}
                        onMouseLeave={handleEntityMouseLeave}
                        onTap={handleEntityTap}
                        stroke={selected ? Color.primary : Color.spaceGray}
                        strokeWidth={selected ? 2 : 1}
                        width={fixture.boardWidth}
                        x={fixture.x + fixture.boardX}
                        y={
                          (isHangcell ? fixture.y - fixture.thickness : fixture.y) +
                          (shelfDragPreview?.activeShelfId === fixture.id
                            ? 0
                            : getShelfDragOffset(fixture.id))
                        }
                      />
                    );
                  })}
              </Group>
              {/* Racks stand behind the stock, as a rack's frame stands behind what it holds. */}
              {(scene.racks ?? []).length > 0 && (
                <Group listening={false} name="planogram-racks">
                  {(scene.racks ?? []).filter(isInRenderWindow).map((rack) => {
                    const upright = Math.min(RACK_UPRIGHT_WIDTH, rack.width / 4);
                    return (
                      <Group key={rack.id} name={`rack-${rack.id}`}>
                        <Rect
                          height={rack.height}
                          name={`rack-frame-${rack.id}`}
                          opacity={0.5}
                          stroke={Color.gray400}
                          strokeWidth={1}
                          width={rack.width}
                          x={rack.x}
                          y={rack.y}
                        />
                        {[rack.x, rack.x + rack.width - upright].map((x, index) => (
                          <Rect
                            fill={RACK_UPRIGHT_FILL}
                            height={rack.height}
                            key={index}
                            name={`rack-upright-${rack.id}-${index}`}
                            opacity={0.85}
                            width={upright}
                            x={x}
                            y={rack.y}
                          />
                        ))}
                        {/* The name sits on the top tier's board, clear of the stock on it. */}
                        <Text
                          align="center"
                          fill={Color.white}
                          fontSize={rack.labelHeight * 0.8}
                          height={rack.labelHeight}
                          name={`rack-label-${rack.id}`}
                          text={rack.name}
                          verticalAlign="middle"
                          width={Math.max(0, rack.width - 2 * upright)}
                          x={rack.x + upright}
                          y={rack.y}
                        />
                      </Group>
                    );
                  })}
                </Group>
              )}
              {scenePlacements.length > 0 && (
                <Group name="planogram-placements">
                  {visiblePlacements.map((placement) => {
                    const selected = selectedPlacementIds.has(placement.id);
                    const asset = placementAssets.get(getPlacementAssetKey(placement));
                    return (
                      <Group
                        draggable={canMove}
                        id={placement.id}
                        key={placement.id}
                        name={`placement-${placement.id}`}
                        onContextMenu={handleEntityContextMenu}
                        onClick={handleEntityClick}
                        onDblClick={handleEntityDoubleClick}
                        onDblTap={handleEntityDoubleTap}
                        onDragEnd={canMove ? handleEntityDragEnd : undefined}
                        onDragStart={canMove ? handleEntityDragStart : undefined}
                        onMouseDown={handleEntityPointerDown}
                        onMouseEnter={handleEntityMouseEnter}
                        onMouseLeave={handleEntityMouseLeave}
                        onTap={handleEntityTap}
                        onTouchStart={handleEntityPointerDown}
                        x={placement.x}
                        y={placement.y}
                      >
                        <PlacementCells
                          asset={asset}
                          color={resolvedView.productColors?.get(placement.id)}
                          displayMode={resolvedView.productDisplayMode}
                          labelFontSize={globalLabelFontSize}
                          placement={placement}
                          scale={activeViewport.scale}
                          showLabel={showProductLabels}
                        />
                        {selected && (
                          <>
                            <Rect
                              height={placement.height}
                              listening={false}
                              stroke={Color.white}
                              strokeWidth={Math.max(6, (worldHeight / 600) * 4)}
                              width={placement.width}
                              x={0}
                              y={0}
                            />
                            <Rect
                              height={placement.height}
                              listening={false}
                              stroke={Color.primary}
                              strokeWidth={Math.max(3, (worldHeight / 600) * 2)}
                              width={placement.width}
                              x={0}
                              y={0}
                            />
                          </>
                        )}
                      </Group>
                    );
                  })}
                </Group>
              )}
              <Group name="planogram-products">
                {visibleProducts.map((product) => {
                  const isActivePreview = productDragPreview?.activeItemId === product.id;
                  const isPreviewMember = productDragPreviewIds.has(product.id);

                  return (
                    <Group
                      dragDistance={GESTURE_THRESHOLD}
                      draggable={canMove}
                      id={product.id}
                      key={product.id}
                      name={`product-${product.id}`}
                      onContextMenu={handleEntityContextMenu}
                      onClick={handleEntityClick}
                      onDblClick={handleEntityDoubleClick}
                      onDblTap={handleEntityDoubleTap}
                      onMouseDown={handleEntityPointerDown}
                      onDragEnd={canMove ? handleEntityDragEnd : undefined}
                      onDragMove={canMove ? handleEntityDragMove : undefined}
                      onDragStart={canMove ? handleEntityDragStart : undefined}
                      onMouseEnter={handleEntityMouseEnter}
                      onMouseLeave={handleEntityMouseLeave}
                      onTap={handleEntityTap}
                      onTouchStart={handleEntityPointerDown}
                      visible={
                        (!isPreviewMember || isActivePreview) &&
                        (isActivePreview || shelfDragPreview !== null || isProductVisible(product))
                      }
                      x={product.x}
                      y={product.y + getShelfDragOffset(product.shelfId)}
                    >
                      <ProductCells
                        asset={assets.get(getAssetKey(product))}
                        color={resolvedView.productColors?.get(product.id)}
                        detail={isProductVisible(product) ? "full" : "reduced"}
                        displayMode={resolvedView.productDisplayMode}
                        labelFontSize={globalLabelFontSize}
                        outlineFacings={resolvedView.outlineFacings}
                        product={product}
                        scale={activeViewport.scale}
                        showLabel={showProductLabels}
                      />
                      {selectedProductIds.has(product.id) && (
                        <>
                          <Rect
                            height={product.height}
                            listening={false}
                            stroke={Color.white}
                            strokeWidth={Math.max(6, (worldHeight / 600) * 4)}
                            width={product.width}
                            x={0}
                            y={0}
                          />
                          <Rect
                            height={product.height}
                            listening={false}
                            stroke={Color.primary}
                            strokeWidth={Math.max(3, (worldHeight / 600) * 2)}
                            width={product.width}
                            x={0}
                            y={0}
                          />
                        </>
                      )}
                      {isActivePreview && (
                        <Group listening={false} name="product-drag-preview">
                          {scene.products
                            .filter(
                              (candidate) =>
                                candidate.id !== product.id &&
                                productDragPreviewIds.has(candidate.id),
                            )
                            .map((candidate) => (
                              <Group
                                key={candidate.id}
                                name={`drag-preview-product-${candidate.id}`}
                                x={candidate.x - product.x}
                                y={candidate.y - product.y}
                              >
                                <ProductCells
                                  asset={assets.get(getAssetKey(candidate))}
                                  color={resolvedView.productColors?.get(candidate.id)}
                                  detail={isProductVisible(candidate) ? "full" : "reduced"}
                                  displayMode={resolvedView.productDisplayMode}
                                  product={candidate}
                                  scale={activeViewport.scale}
                                  showLabel={showProductLabels}
                                />
                                <Rect
                                  height={candidate.height}
                                  listening={false}
                                  stroke={Color.primary}
                                  strokeWidth={2}
                                  width={candidate.width}
                                />
                              </Group>
                            ))}
                        </Group>
                      )}
                    </Group>
                  );
                })}
              </Group>
              {dropPreview && (
                <Group listening={false} name="planogram-drop-preview">
                  <Rect
                    dash={[6, 4]}
                    fill={Color.primary}
                    height={dropPreview.height}
                    listening={false}
                    name="drop-preview-ghost"
                    opacity={0.35}
                    stroke={Color.primary}
                    strokeWidth={2}
                    width={dropPreview.width}
                    x={dropPreview.x}
                    y={dropPreview.y}
                  />
                </Group>
              )}
              {dropHint && (
                <Group listening={false} name="planogram-drop-hint">
                  <Rect
                    dash={[6, 4]}
                    fill={Color.primary}
                    height={dropHint.height}
                    listening={false}
                    name="drop-hint-ghost"
                    opacity={0.22}
                    stroke={Color.primary}
                    strokeWidth={1 / activeViewport.scale}
                    width={dropHint.width}
                    x={dropHint.caretX}
                    y={dropHint.y}
                  />
                  <Rect
                    fill={Color.primary}
                    height={dropHint.height}
                    listening={false}
                    name="drop-hint-caret"
                    width={3 / activeViewport.scale}
                    x={dropHint.caretX - 1.5 / activeViewport.scale}
                    y={dropHint.y}
                  />
                </Group>
              )}
              {[...scene.shelves, ...scene.fixtures].some(
                (shelf) => shelf.flaggedDeadSpans.length > 0 || shelf.rackCollides,
              ) && (
                <Group listening={false} name="planogram-non-merchandisable-areas">
                  {scene.shelves
                    .filter((shelf) => shelf.rackCollides)
                    .map((shelf) => (
                      <Rect
                        dash={[8, 5]}
                        fill={Color.alertMediumBg}
                        height={shelf.thickness}
                        key={`rack-collision-${shelf.id}`}
                        name={`rack-collision-${shelf.id}`}
                        opacity={0.85}
                        stroke={Color.alertMediumIcon}
                        strokeWidth={1}
                        width={shelf.boardWidth}
                        x={shelf.x + shelf.boardX}
                        y={shelf.y}
                      />
                    ))}
                  {[...scene.shelves, ...scene.fixtures]
                    .filter((shelf) => shelf.flaggedDeadSpans.length > 0)
                    .map((shelf) => {
                      // HANGCELL y/height is the hang zone; the thickness band sits above that y.
                      const isHangcell = "fixtureType" in shelf && shelf.fixtureType === "HANGCELL";
                      const barY = isHangcell ? shelf.y - shelf.thickness : shelf.y;

                      return shelf.flaggedDeadSpans.map((span, index) => {
                        const suffix = index === 0 ? "" : `-${index}`;
                        // A hangcell's zone hangs below it; any other board's rises to the
                        // lowest board live over the span.
                        const extensionHeight = isHangcell
                          ? shelf.nonMerchandisableExtensionHeight
                          : span.extensionHeight;
                        const extensionY = isHangcell
                          ? shelf.nonMerchandisableExtensionY
                          : span.extensionY;
                        return (
                          <Fragment key={`${shelf.id}${suffix}`}>
                            <Rect
                              dash={[8, 5]}
                              fill={Color.alertMediumBg}
                              height={shelf.thickness}
                              name={`non-merchandisable-area-${shelf.id}${suffix}`}
                              opacity={0.85}
                              stroke={Color.alertMediumIcon}
                              strokeWidth={1}
                              width={span.width}
                              x={shelf.x + span.x}
                              y={barY}
                            />
                            {extensionHeight > 0 && (
                              <Rect
                                dash={[8, 5]}
                                fill={Color.alertMediumBg}
                                height={extensionHeight}
                                name={`non-merchandisable-area-extension-${shelf.id}${suffix}`}
                                opacity={0.3}
                                stroke={Color.alertMediumIcon}
                                strokeWidth={1}
                                width={span.width}
                                x={shelf.x + span.x}
                                y={extensionY}
                              />
                            )}
                          </Fragment>
                        );
                      });
                    })}
                </Group>
              )}
            </Layer>
            <PresentationOverlays
              entities={visibleOverlayEntities}
              onBadgeActivate={onOverlayBadgeActivate}
              presentations={overlayPresentations.filter((presentation) =>
                visibleOverlayIds.has(presentation.id),
              )}
              scale={activeViewport.scale}
            />
            <FrontOverhangMarkers
              products={visibleProducts.filter(
                (product) =>
                  !productDragPreviewIds.has(product.id) &&
                  !shelfDragPreviewIds.has(product.shelfId),
              )}
              shelves={scene.shelves.filter(
                (shelf) => isInRenderWindow(shelf) && !shelfDragPreviewIds.has(shelf.id),
              )}
              scale={activeViewport.scale}
            />
            {/* Its own top layer: above product images and the highlight / de-emphasis washes. */}
            {resolvedView.showDividers !== false && scene.dividers && scene.dividers.length > 0 && (
              <Layer listening={false} name="planogram-shelf-dividers-layer">
                <Group listening={false} name="planogram-shelf-dividers">
                  {scene.dividers.map((divider) => {
                    const span = getDividerDrawSpan(divider.x, divider.width, activeViewport.scale);
                    return (
                      <Rect
                        fill={SHELF_DIVIDER_COLOR}
                        height={divider.height}
                        key={divider.id}
                        listening={false}
                        name={`shelf-divider-${divider.id}`}
                        perfectDrawEnabled={false}
                        width={span.width}
                        x={span.x}
                        y={divider.y + getShelfDragOffset(divider.shelfId)}
                      />
                    );
                  })}
                </Group>
              </Layer>
            )}
          </Stage>
          {renderedWorldOverlay && (
            <WorldOverlay
              ref={worldOverlayRef}
              $height={worldHeight}
              $width={worldWidth}
              $viewport={activeViewport}
              data-testid="konva-planogram-world-overlay"
            >
              {renderedWorldOverlay}
            </WorldOverlay>
          )}
          {aisleBreakEditing && hoveredSeam && hoveredSeamView && (
            <AisleBreakSeamHandle
              editing={aisleBreakEditing}
              labels={labels}
              seam={hoveredSeamView}
              x={hoveredSeamView.x * activeViewport.scale + activeViewport.offsetX}
              y={hoveredSeam.worldY * activeViewport.scale + activeViewport.offsetY}
            />
          )}
          <KonvaPlanogramViewportChrome
            labels={labels}
            onReset={resetViewport}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
          />
        </StageViewport>
      </CanvasSurface>
      {accessibleControls}
      {aisleBreakEditing && (
        // The seam handle for the keyboard: one button per seam, never after the last bay.
        <AccessibleControls aria-label={labels.aisleBreak} role="list">
          {seams.map((seam) => (
            <AccessibleProductGroup key={seam.bayId} role="listitem">
              <AccessibleProductButton
                data-testid={`aisle-break-seam-button-${seam.bayId}`}
                onClick={() => aisleBreakEditing.onSetSplitAfter(seam.bayId, !seam.isBreak)}
                tabIndex={0}
                type="button"
              >
                {seam.isBreak
                  ? labels.joinBays(seam.bayNo, seam.bayNo + 1)
                  : labels.breakTheAisleBetween(seam.bayNo, seam.bayNo + 1)}
              </AccessibleProductButton>
            </AccessibleProductGroup>
          ))}
        </AccessibleControls>
      )}
    </CanvasRoot>
  );
};
